import React, { useState, useEffect, useCallback, useMemo } from 'react';

import { 
  UI_IMAGES, 
  BACKGROUND_IMAGE, 
  getSpeciesImage, 
  getSpeciesColor 
} from '../utils/AssetManager';

import { DIFFICULTY, SPECIES, MUTATIONS, EVENTS } from '../data/GameData';


// ============ ASSET CONFIGURATION ============
// For standalone artifact: set to null (uses procedural gradient)
// For full project with images: set to '/assets/backgrounds/petri-dish.jpg'


// ============ MAP GENERATORS ============

// Helper: Check if two line segments intersect (for sterile lines)
const doLinesIntersect = (p1, p2, p3, p4) => {
  const det = (p2.x - p1.x) * (p4.y - p3.y) - (p4.x - p3.x) * (p2.y - p1.y);
  if (det === 0) return false; // Parallel lines

  const lambda = ((p4.y - p3.y) * (p4.x - p1.x) + (p3.x - p4.x) * (p4.y - p1.y)) / det;
  const gamma = ((p1.y - p2.y) * (p4.x - p1.x) + (p2.x - p1.x) * (p4.y - p1.y)) / det;

  return (0 < lambda && lambda < 1) && (0 < gamma && gamma < 1);
};

// Generate random sterile lines for outbreak mode
const generateSterileLines = (width, height, count = 4) => {
  const barriers = [];
  const centerX = width / 2;
  const centerY = height / 2;
  const maxRadius = (width / 2) - 150;
  
  // Divide the dish into sectors to spread lines evenly
  const sectorAngle = (Math.PI * 2) / count;
  
  for (let i = 0; i < count; i++) {
    // Base angle for this sector, with some randomness
    const baseAngle = i * sectorAngle + (Math.random() - 0.5) * sectorAngle * 0.6;
    
    // Random distance from center - spread across the dish
    const dist = maxRadius * (0.25 + Math.random() * 0.55);
    
    // Line center point - positioned outward from center
    const lineCenterX = centerX + Math.cos(baseAngle) * dist;
    const lineCenterY = centerY + Math.sin(baseAngle) * dist;
    
    // Line length - much longer and more varied (300-600 for 1600px map)
    const lineLength = 300 + Math.random() * 300;
    
    // Random angle for the line itself (full rotation possible)
    const lineAngle = Math.random() * Math.PI;
    
    barriers.push({
      start: {
        x: lineCenterX - Math.cos(lineAngle) * lineLength / 2,
        y: lineCenterY - Math.sin(lineAngle) * lineLength / 2,
      },
      end: {
        x: lineCenterX + Math.cos(lineAngle) * lineLength / 2,
        y: lineCenterY + Math.sin(lineAngle) * lineLength / 2,
      }
    });
  }
  
  return barriers;
};

const generateLargeMap = (width, height, numTerritories, mapType) => {
  const seeds = [];
  const padding = 45;
  const minDistance = mapType === 'outbreak' ? 90 : mapType === 'chokepoint' ? 60 : 65;
  const maxAttempts = 500;
  
  // Generate sterile lines for outbreak mode
  let barriers = [];
  if (mapType === 'outbreak'){
    barriers = generateSterileLines(width, height, 4);
  }
  
  if (mapType === 'outbreak' || mapType === 'open'){
    // Large map generation with sterile line avoidance
    const centerX = width / 2;
    const centerY = height / 2;
    const dishRadius = (width / 2) - 100;
    
    let attempts = 0;
    while (seeds.length < numTerritories && attempts < maxAttempts * numTerritories) {
      const candidate = {
        x: padding + Math.random() * (width - padding * 2),
        y: padding + Math.random() * (height - padding * 2),
      };
      
      // Check if inside dish
      const distFromCenter = Math.hypot(candidate.x - centerX, candidate.y - centerY);
      if (distFromCenter > dishRadius) {
        attempts++;
        continue;
      }
      
      // Check if too close to sterile lines
      let tooCloseToBarrier = false;
      for (const bar of barriers) {
        const midX = (bar.start.x + bar.end.x) / 2;
        const midY = (bar.start.y + bar.end.y) / 2;
        const distToMid = Math.hypot(candidate.x - midX, candidate.y - midY);
        const distToStart = Math.hypot(candidate.x - bar.start.x, candidate.y - bar.start.y);
        const distToEnd = Math.hypot(candidate.x - bar.end.x, candidate.y - bar.end.y);
        
        if (distToMid < 40 || distToStart < 40 || distToEnd < 40) {
          tooCloseToBarrier = true;
          break;
        }
      }
      if (tooCloseToBarrier) {
        attempts++;
        continue;
      }
      
      // Check distance from other seeds
      let tooClose = false;
      for (const seed of seeds) {
        if (Math.hypot(candidate.x - seed.x, candidate.y - seed.y) < minDistance) {
          tooClose = true;
          break;
        }
      }
      
      if (!tooClose) seeds.push(candidate);
      attempts++;
    }
    
    // Attach barriers to seeds for later use
    seeds._barriers = barriers;
    return seeds;
  }
  
  if (mapType === 'chokepoint') {
    // 4 corners with central chokepoint
    const corners = [
      { x: 80, y: 100 },
      { x: width - 80, y: 100 },
      { x: 80, y: height - 100 },
      { x: width - 80, y: height - 100 },
    ];
    
    // Corner clusters
    corners.forEach(corner => {
      for (let i = 0; i < 5; i++) {
        seeds.push({
          x: corner.x + (Math.random() - 0.5) * 100,
          y: corner.y + (Math.random() - 0.5) * 100,
        });
      }
    });
    
    // Central corridor
    for (let i = 0; i < 6; i++) {
      seeds.push({
        x: width / 2 + (Math.random() - 0.5) * 80,
        y: height / 2 + (Math.random() - 0.5) * 150,
      });
    }
  } else if (mapType === 'ring') {
    const centerX = width / 2;
    const centerY = height / 2;
    
    // Outer ring - starting positions
    for (let i = 0; i < 16; i++) {
      const angle = (i / 16) * Math.PI * 2;
      const radius = 200 + Math.random() * 30;
      seeds.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    }
    
    // Middle ring
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2 + 0.2;
      const radius = 120 + Math.random() * 20;
      seeds.push({
        x: centerX + Math.cos(angle) * radius,
        y: centerY + Math.sin(angle) * radius,
      });
    }
    
    // Center prize
    seeds.push({ x: centerX, y: centerY });
    seeds.push({ x: centerX - 40, y: centerY - 30 });
    seeds.push({ x: centerX + 40, y: centerY - 30 });
    seeds.push({ x: centerX, y: centerY + 45 });
// ... inside generateLargeMap function ...

  } else {
    // Open map with Poisson disk sampling (CIRCULAR CONSTRAINED)
    let attempts = 0;
    const centerX = width / 2;
    const centerY = height / 2;
    
    // ADJUST THIS NUMBER:
    // If width is 600, half is 300. 
    // If your dish image has black borders, try 280 or 270 to keep dots inside the glass.
    const dishRadius = (width / 2) - 85; 

    while (seeds.length < numTerritories && attempts < maxAttempts * numTerritories) {
      const candidate = {
        x: padding + Math.random() * (width - padding * 2),
        y: padding + Math.random() * (height - padding * 2),
      };
      
      // CHECK: Is this point inside the visual dish?
      const distFromCenter = Math.hypot(candidate.x - centerX, candidate.y - centerY);
      
      // If the point is outside our defined dish radius, skip it!
      if (distFromCenter > dishRadius) {
        attempts++;
        continue; 
      }

      // Existing check for distance between colonies
      let tooClose = false;
      for (const seed of seeds) {
        if (Math.hypot(candidate.x - seed.x, candidate.y - seed.y) < minDistance) {
          tooClose = true;
          break;
        }
      }
      
      if (!tooClose) seeds.push(candidate);
      attempts++;
    }
  }
  
  return seeds;
};

const assignTerrain = (territories, mapType) => {
  territories.forEach((t) => {
    const roll = Math.random();
    
    if (mapType === 'chokepoint') {
      const isMiddle = t.x > 150 && t.x < 300;
      if (isMiddle) {
        t.terrain = roll < 0.4 ? 'fortified' : roll < 0.7 ? 'toxic' : 'normal';
      } else {
        t.terrain = roll < 0.2 ? 'nutrient' : roll < 0.35 ? 'symbiote' : 'normal';
      }
    } else if (mapType === 'ring') {
      const distFromCenter = Math.hypot(t.x - 225, t.y - 300);
      if (distFromCenter < 80) {
        t.terrain = roll < 0.5 ? 'nutrient' : roll < 0.8 ? 'spore' : 'fortified';
      } else {
        t.terrain = roll < 0.15 ? 'nutrient' : roll < 0.3 ? 'symbiote' : 'normal';
      }
    } else {
      if (roll < 0.12) t.terrain = 'nutrient';
      else if (roll < 0.22) t.terrain = 'fortified';
      else if (roll < 0.32) t.terrain = 'toxic';
      else if (roll < 0.40) t.terrain = 'spore';
      else if (roll < 0.48) t.terrain = 'symbiote';
      else t.terrain = 'normal';
    }
  });
  
  return territories;
};

const generateMap = (width, height, mapType = 'open', numPlayers = 4) => {
  // Adjust territory count based on map type
  const numTerritories = mapType === 'outbreak' ? 90 : mapType === 'ring' ? 32 : 35;
  
  const seeds = generateLargeMap(width, height, numTerritories, mapType);
  const barriers = seeds._barriers || [];
 
  const territories = seeds.map((seed, i) => {
    const neighbors = [];
    
    // Calculate distances to all other points
    const distances = seeds
      .map((other, j) => ({ index: j, dist: Math.hypot(seed.x - other.x, seed.y - other.y) }))
      .filter(d => d.index !== i)
      .sort((a, b) => a.dist - b.dist);
    
    // Connection range varies by map type
    const maxDist = mapType === 'outbreak' ? 200 : mapType === 'chokepoint' ? 130 : 180;
    
    // Connect to nearby neighbors (with barrier checking for outbreak)
    for (let n = 0; n < Math.min(5, distances.length); n++) {
      if (distances[n].dist > maxDist) break;
      
      const targetSeed = seeds[distances[n].index];
      
      // Check if connection crosses a barrier
      let blocked = false;
      if (mapType === 'outbreak' || mapType === 'open'){
        for (const bar of barriers) {
          if (doLinesIntersect(bar.start, bar.end, seed, targetSeed)) {
            blocked = true;
            break;
          }
        }
      }
      
      if (!blocked) {
        neighbors.push(distances[n].index);
      }
    }

    // Safety link: If isolated, connect to closest non-blocked neighbor
    if (neighbors.length === 0 && distances.length > 0) {
      for (let n = 0; n < distances.length; n++) {
        const targetSeed = seeds[distances[n].index];
        let blocked = false;
        
        if (mapType === 'outbreak' || mapType === 'open'){
          for (const bar of barriers) {
            if (doLinesIntersect(bar.start, bar.end, seed, targetSeed)) {
              blocked = true;
              break;
            }
          }
        }
        
        if (!blocked) {
          neighbors.push(distances[n].index);
          break;
        }
      }
    }
    
    return {
      id: i,
      x: seed.x,
      y: seed.y,
      neighbors,
      owner: null,
      biomass: 0,
      terrain: 'normal',
      radius: mapType === 'outbreak' ? 18 + Math.random() * 6 : 22 + Math.random() * 8,
    };
  });
  
  // Bidirectional neighbors (Ensure if A connects to B, B connects to A)
  territories.forEach((t, i) => {
    t.neighbors.forEach(n => {
      if (!territories[n].neighbors.includes(i)) {
        territories[n].neighbors.push(i);
      }
    });
  });
  
  assignTerrain(territories, mapType);
  
  // Attach barriers for rendering
  territories._barriers = barriers;
  
  return territories;
};

// ============ GAME SETUP ============

const setupGame = (territories, playerSpecies, aiSpecies, mapType, difficulty = 'normal') => {
  const updated = [...territories];
  const allPlayers = [playerSpecies, ...aiSpecies];
  const diffSettings = DIFFICULTY[difficulty] || DIFFICULTY.normal;
  
  // 1. Determine Map Center and Size automatically
  // (We calculate this so it works even if you change map size later)
  const xs = territories.map(t => t.x);
  const ys = territories.map(t => t.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  
  // We want to find start spots near the outer rim
  const spawnRadius = (maxX - minX) / 2.5; 

  // 2. Determine Start Positions
  let startPositions = [];

  // Random rotation so Player 1 isn't always at the "Top"
  const angleOffset = Math.random() * Math.PI * 2;

  if (mapType === 'chokepoint') {
    // Keep your specific logic for chokepoint if you want, 
    // or just let the code below handle it dynamically.
    // For now, let's use the dynamic logic for everything except 'ring'
    // which has special needs.
  }
  
  // Logic for Open, Ring, and Chokepoint (Dynamic Spacing)
  // Divide the circle into equal segments based on player count
  startPositions = allPlayers.map((_, index) => {
    // Calculate the ideal "Perfect Spot" for this player
    const angle = angleOffset + (index * (Math.PI * 2 / allPlayers.length));
    
    const idealX = centerX + Math.cos(angle) * spawnRadius;
    const idealY = centerY + Math.sin(angle) * spawnRadius;

    // Find the actual territory closest to this ideal spot
    // that hasn't been taken yet
    const bestTerritory = updated
      .filter(t => !t.owner)
      .sort((a, b) => {
        const distA = Math.hypot(a.x - idealX, a.y - idealY);
        const distB = Math.hypot(b.x - idealX, b.y - idealY);
        return distA - distB;
      })[0];
      
    return bestTerritory;
  });
  
  // 3. Assign Territories
  allPlayers.forEach((species, i) => {
    const startNode = startPositions[i];
    const isAI = species !== playerSpecies;
    const bonusBiomass = isAI ? diffSettings.startingBonus : 0;
    
    if (startNode) {
      startNode.owner = species;
      startNode.biomass = 8 + bonusBiomass;
      
      // Give second adjacent territory (closest one)
      // We sort neighbors by distance to ensure we pick the closest connected one
      const neighborId = startNode.neighbors
        .sort((a, b) => {
          const nA = updated.find(t => t.id === a);
          const nB = updated.find(t => t.id === b);
          const distA = Math.hypot(nA.x - startNode.x, nA.y - startNode.y);
          const distB = Math.hypot(nB.x - startNode.x, nB.y - startNode.y);
          return distA - distB;
        })
        .find(n => !updated.find(t => t.id === n).owner);

      if (neighborId !== undefined) {
        const neighbor = updated.find(t => t.id === neighborId);
        neighbor.owner = species;
        neighbor.biomass = 5 + bonusBiomass;
      }
    }
  });
  
  return updated;
};

// ============ VISUALS ============

const generateBlobPath = (x, y, radius, seed) => {
  const points = 8;
  const path = [];
  
  for (let i = 0; i < points; i++) {
    const angle = (i / points) * Math.PI * 2;
    const wobble = 0.7 + 0.3 * Math.sin(seed * 10 + i * 2.5);
    const r = radius * wobble;
    path.push({
      x: x + Math.cos(angle) * r,
      y: y + Math.sin(angle) * r,
    });
  }
  
  let d = `M ${path[0].x} ${path[0].y}`;
  for (let i = 0; i < points; i++) {
    const p0 = path[i];
    const p1 = path[(i + 1) % points];
    const mx = (p0.x + p1.x) / 2;
    const my = (p0.y + p1.y) / 2;
    d += ` Q ${p0.x} ${p0.y} ${mx} ${my}`;
  }
  d += ' Z';
  
  return d;
};

const getTerrainIcon = (terrain) => {
  switch (terrain) {
    case 'nutrient': return '★';
    case 'fortified': return '🛡️';
    case 'toxic': return '☠️';
    case 'spore': return '🎯';
    case 'symbiote': return '🔗';
    default: return null;
  }
};

const getTerrainGlow = (terrain) => {
  switch (terrain) {
    case 'nutrient': return 'rgba(255, 220, 100, 0.3)';
    case 'fortified': return 'rgba(100, 150, 255, 0.25)';
    case 'toxic': return 'rgba(150, 50, 200, 0.25)';
    case 'spore': return 'rgba(255, 100, 100, 0.25)';
    case 'symbiote': return 'rgba(100, 255, 150, 0.25)';
    default: return null;
  }
};

// ============ COMBAT ============

const rollDice = (n) => {
  const rolls = [];
  for (let i = 0; i < n; i++) {
    rolls.push(Math.floor(Math.random() * 6) + 1);
  }
  return rolls.sort((a, b) => b - a);
};

const resolveCombatRound = (attackerCount, defenderCount, attackerDice = 3, defenderDice = 2) => {
  const attackDice = rollDice(Math.min(attackerCount, attackerDice));
  const defenseDice = rollDice(Math.min(defenderCount, defenderDice));
  
  let attackerLosses = 0;
  let defenderLosses = 0;
  
  const comparisons = Math.min(attackDice.length, defenseDice.length);
  for (let i = 0; i < comparisons; i++) {
    if (attackDice[i] > defenseDice[i]) {
      defenderLosses++;
    } else {
      attackerLosses++;
    }
  }
  
  return { attackerLosses, defenderLosses, attackDice, defenseDice };
};

const getSpeciesMatchupModifier = (attackerSpecies, defenderSpecies) => {
  const attacker = SPECIES[attackerSpecies];
  const defender = SPECIES[defenderSpecies];
  if (!attacker || !defender) return 0;
  
  if (attacker.strongVs.includes(defenderSpecies)) return 0.20;
  if (attacker.weakVs.includes(defenderSpecies)) return -0.20;
  return 0;
};

const calculateDetailedWinProbability = (
  attackers, 
  defenders, 
  attackerSpecies, 
  defenderSpecies,
  attackerMutations = [],
  defenderMutations = [],
  defenderTerrain = 'normal',
  pincerCount = 0, // Number of other friendly territories adjacent to target
  isDeterministic = false // <--- NEW FLAG ADDED HERE
  
) => {
  if (attackers <= 0) return { probability: 0, attackerEffective: 0, defenderDice: 2, notes: [], matchup: 0 };
  
  const notes = [];
  let effectiveAttackers = attackers;

// === 1. DETERMINISTIC MODE (Geographical Strategy) ===
  if (isDeterministic) {
    // A. Volatile Terrain (The "Anti-Shield")
    // If target is Toxic, it's unstable/volatile, giving the attacker an advantage.
    if (defenderTerrain === 'toxic') {
      effectiveAttackers += 1; // <--- CHANGED: Now a BONUS instead of a penalty
      notes.push('☠️ +1 (volatile)');
    }

    const attackPower = effectiveAttackers;
    let defensePower = defenders;

    // B. Fortified Terrain (The Shield)
    if (defenderTerrain === 'fortified') {
      defensePower += 1;
      notes.push('🛡️ +1 fort');
    }

    // C. The Math (Simple Subtraction)
    const diff = attackPower - defensePower;

    if (diff > 0) {
      return { 
        probability: 100, 
        attackerEffective: attackPower, 
        defenderDice: defensePower, 
        notes, 
        deterministicResult: 'win', 
        survivors: diff 
      };
    } else if (diff === 0) {
      return { 
        probability: 0, 
        attackerEffective: attackPower, 
        defenderDice: defensePower, 
        notes, 
        deterministicResult: 'draw', 
        survivors: 0 
      };
    } else {
      return { 
        probability: 0, 
        attackerEffective: attackPower, 
        defenderDice: defensePower, 
        notes, 
        deterministicResult: 'loss', 
        survivors: 0 
      };
    }
  }
  
  // Species matchup
  const matchup = getSpeciesMatchupModifier(attackerSpecies, defenderSpecies);
  if (matchup > 0) notes.push(`💪 +${Math.round(matchup * 100)}% vs ${SPECIES[defenderSpecies]?.name}`);
  if (matchup < 0) notes.push(`😰 ${Math.round(matchup * 100)}% vs ${SPECIES[defenderSpecies]?.name}`);
  
  // Pre-combat losses (toxic terrain or Mucor passive)
  const defenderIsMucor = defenderSpecies === 'mucor';
  if (defenderTerrain === 'toxic' || defenderIsMucor) {
    effectiveAttackers = Math.max(1, effectiveAttackers - 1);
    notes.push(defenderIsMucor ? '🟡 -1 (Mucor aura)' : '☠️ -1 (toxic)');
  }
  if (defenderMutations.includes('toxic_skin')) {
    effectiveAttackers = Math.max(1, effectiveAttackers - 1);
    notes.push('🧪 -1 (toxic skin)');
  }

  
  
  // Attacker dice (Aspergillus gets 4)
  let attackerDice = attackerSpecies === 'aspergillus' ? 4 : 3;
  if (attackerSpecies === 'aspergillus') notes.push('🟠 4 atk dice');
  
  // Trichoderma passive: +1 attack die (mycoparasite)
  if (attackerSpecies === 'trichoderma') {
    attackerDice += 1;
    notes.push('💚 +1 atk die (mycoparasite)');
  }
  
  // Pincer bonus: +1 die if 2+ allies adjacent, +2 if 4+
  let pincerBonus = 0;
  if (pincerCount >= 4) {
    pincerBonus = 2;
    attackerDice += 2;
    notes.push('🔱 Pincer +2 dice');
  } else if (pincerCount >= 2) {
    pincerBonus = 1;
    attackerDice += 1;
    notes.push('🔱 Pincer +1 die');
  }
  
  // Defender dice
  let defenderDice = 2;
  if (defenderTerrain === 'fortified') {
    defenderDice = 3;
    notes.push('🛡️ fortified');
  }
  if (defenderSpecies === 'candida') {
    defenderDice += 1;
    notes.push('🩷 +1 def die');
  }
  if (defenderMutations.includes('thick_walls')) {
    defenderDice += 1;
    notes.push('🧱 +1 def die');
  }
  
  // Calculate probability
  const ratio = effectiveAttackers / defenders;
  let probability;
  
  const diceAdvantage = attackerDice - defenderDice;
  
  if (diceAdvantage >= 3) {
    if (ratio > 2) probability = 0.95;
    else if (ratio > 1.5) probability = 0.85;
    else if (ratio > 1) probability = 0.70;
    else if (ratio > 0.7) probability = 0.50;
    else probability = 0.35;
  } else if (diceAdvantage >= 2) {
    if (ratio > 2) probability = 0.90;
    else if (ratio > 1.5) probability = 0.75;
    else if (ratio > 1) probability = 0.60;
    else if (ratio > 0.7) probability = 0.40;
    else probability = 0.25;
  } else if (diceAdvantage >= 0) {
    if (ratio > 2) probability = 0.85;
    else if (ratio > 1.5) probability = 0.70;
    else if (ratio > 1) probability = 0.55;
    else if (ratio > 0.7) probability = 0.35;
    else probability = 0.20;
  } else {
    if (ratio > 2) probability = 0.75;
    else if (ratio > 1.5) probability = 0.55;
    else if (ratio > 1) probability = 0.40;
    else if (ratio > 0.7) probability = 0.25;
    else probability = 0.10;
  }
  
  // Apply matchup modifier
  probability = Math.max(0.05, Math.min(0.95, probability + matchup));
  
  return {
    probability: Math.round(probability * 100),
    attackerEffective: effectiveAttackers,
    attackerDice,
    defenderDice,
    notes,
    matchup,
    pincerBonus
  };
};

// Quick AI probability calc
// REPLACE calculateWinProbability WITH THIS:
const calculateWinProbability = (attackers, defenders, attackerSpecies, defenderSpecies, defenderTerrain, pincerCount = 0, isDeterministic = false) => {
  const result = calculateDetailedWinProbability(attackers - 1, defenders, attackerSpecies, defenderSpecies, [], [], defenderTerrain, pincerCount, isDeterministic);
  return result.probability / 100;
};


// ============ MAIN COMPONENT ============

const FungalConquest = () => {
  const lastSpectatorTrigger = React.useRef(null);
  // Game setup state
  const [gamePhase, setGamePhase] = useState('menu'); // menu, difficulty, species, playing
  const [mapType, setMapType] = useState('open');
  const [difficulty, setDifficulty] = useState('normal');
  const [playerSpecies, setPlayerSpecies] = useState(null);
  const [aiSpecies, setAiSpecies] = useState([]);
  const [combatMode, setCombatMode] = useState('probabilistic');
  
  // DEV TOOL: Spectator mode - watch AI vs AI
  const [isSpectatorMode, setIsSpectatorMode] = useState(false);
  
  // Game state
  const [territories, setTerritories] = useState([]);
  const [selected, setSelected] = useState(null);
  const [target, setTarget] = useState(null);
  const [turn, setTurn] = useState(1);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [message, setMessage] = useState('');
  const [gameOver, setGameOver] = useState(null);
  const [showMoveSelector, setShowMoveSelector] = useState(false);
  const [moveAmount, setMoveAmount] = useState(1);
  const [combatLog, setCombatLog] = useState([]);
  const [isInCombat, setIsInCombat] = useState(false);
  const [aiAction, setAiAction] = useState(null);
  
  // Progression
  const [mutations, setMutations] = useState({});
  const [showMutationPicker, setShowMutationPicker] = useState(false);
  const [mutationChoices, setMutationChoices] = useState([]);
  const [nutrientsCaptured, setNutrientsCaptured] = useState({});
  
  // Events
  const [currentEvent, setCurrentEvent] = useState(null);
  const [showEvent, setShowEvent] = useState(false);
  
  // Rhizopus free expand tracking
  const [usedFreeExpand, setUsedFreeExpand] = useState(false);
  
  // Species Codex
  const [showCodex, setShowCodex] = useState(false);
  const [selectedCodexSpecies, setSelectedCodexSpecies] = useState(null);
  
  // Outbreak mode: sterile barriers
  const [barriers, setBarriers] = useState([]);
  
  // Outbreak mode: viewport panning and zoom
  const [viewportOffset, setViewportOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [zoomLevel, setZoomLevel] = useState(1); // 0.25 = zoomed out (mobile), 1 = normal, 2 = zoomed in
  
  // Touch/pinch zoom state
  const [isPinching, setIsPinching] = useState(false);
  const [lastPinchDistance, setLastPinchDistance] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile device on mount
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 850 || 'ontouchstart' in window;
      setIsMobile(mobile);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Supply Lines: { sourceId: destinationId } - routes growth from source to destination
  const [supplyRoutes, setSupplyRoutes] = useState({});
  const [isSettingSupplyRoute, setIsSettingSupplyRoute] = useState(false); // UI mode for setting routes
  
  // Game Statistics for scoring
  const [gameStats, setGameStats] = useState({
    attacksWon: 0,
    attacksLost: 0,
    territoriesCaptured: 0,
    maxTerritories: 0,
    enemiesEliminated: 0,
    peakBiomass: 0,
  });
  
  // Persistent stats (localStorage)
  const [careerStats, setCareerStats] = useState(() => {
    try {
      const saved = localStorage.getItem('fungalConquest_careerStats');
      return saved ? JSON.parse(saved) : {
        gamesPlayed: 0,
        wins: 0,
        losses: 0,
        byDifficulty: { easy: { wins: 0, losses: 0 }, normal: { wins: 0, losses: 0 }, hard: { wins: 0, losses: 0 }, insane: { wins: 0, losses: 0 } },
        bySpecies: {},
        totalTerritoriesCaptured: 0,
        totalEnemiesEliminated: 0,
      };
    } catch { return { gamesPlayed: 0, wins: 0, losses: 0, byDifficulty: { easy: { wins: 0, losses: 0 }, normal: { wins: 0, losses: 0 }, hard: { wins: 0, losses: 0 }, insane: { wins: 0, losses: 0 } }, bySpecies: {}, totalTerritoriesCaptured: 0, totalEnemiesEliminated: 0 }; }
  });
  
// Responsive: scale to fit screen on mobile, stay 800x800 on desktop
const [dimensions, setDimensions] = useState({ width: 800, height: 800 });

useEffect(() => {
  const updateDimensions = () => {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    
    // On mobile (screen < 850px), scale down to fit
    if (screenWidth < 850) {
      const maxSize = Math.min(screenWidth - 24, screenHeight - 250); // padding + UI space
      setDimensions({ width: maxSize, height: maxSize });
    } else {
      // Desktop: keep original size
      setDimensions({ width: 800, height: 800 });
    }
  };
  
  updateDimensions();
  window.addEventListener('resize', updateDimensions);
  return () => window.removeEventListener('resize', updateDimensions);
}, []);

const mapWidth = dimensions.width;
const mapHeight = dimensions.height;
const scale = mapWidth / 800; // Scale factor for positioning

  // ============ SCORING SYSTEM ============
  
  const calculateGameScore = useCallback((stats, won, turnsPlayed, difficultyId) => {
    const diffMultiplier = { easy: 1, normal: 1.5, hard: 2, insane: 3 }[difficultyId] || 1;
    
    let score = 0;
    score += stats.territoriesCaptured * 100;
    score += stats.attacksWon * 50;
    score += stats.enemiesEliminated * 500;
    score += stats.maxTerritories * 25;
    
    // Bonus for winning
    if (won) score += 1000;
    
    // Speed bonus (fewer turns = more points)
    const speedBonus = Math.max(0, 500 - turnsPlayed * 10);
    score += speedBonus;
    
    // Apply difficulty multiplier
    score = Math.round(score * diffMultiplier);
    
    return score;
  }, []);
  
  const saveCareerStats = useCallback((won) => {
    if (isSpectatorMode) return; // Don't save spectator games
    
    setCareerStats(prev => {
      const updated = {
        ...prev,
        gamesPlayed: prev.gamesPlayed + 1,
        wins: prev.wins + (won ? 1 : 0),
        losses: prev.losses + (won ? 0 : 1),
        byDifficulty: {
          ...prev.byDifficulty,
          [difficulty]: {
            wins: (prev.byDifficulty[difficulty]?.wins || 0) + (won ? 1 : 0),
            losses: (prev.byDifficulty[difficulty]?.losses || 0) + (won ? 0 : 1),
          }
        },
        bySpecies: {
          ...prev.bySpecies,
          [playerSpecies]: {
            wins: (prev.bySpecies[playerSpecies]?.wins || 0) + (won ? 1 : 0),
            losses: (prev.bySpecies[playerSpecies]?.losses || 0) + (won ? 0 : 1),
          }
        },
        totalTerritoriesCaptured: prev.totalTerritoriesCaptured + gameStats.territoriesCaptured,
        totalEnemiesEliminated: prev.totalEnemiesEliminated + gameStats.enemiesEliminated,
      };
      
      try {
        localStorage.setItem('fungalConquest_careerStats', JSON.stringify(updated));
      } catch (e) { /* localStorage unavailable */ }
      
      return updated;
    });
  }, [isSpectatorMode, difficulty, playerSpecies, gameStats]);
  
  // ============ START GAME ============
  
  const selectMap = (type) => {
    setMapType(type);
    setGamePhase('difficulty');
  };
  
  const selectDifficulty = (diff) => {
    setDifficulty(diff);
    if (isSpectatorMode) {
      startSpectatorGame(diff);
    } else {
      setGamePhase('species');
    }
  };
  
  // SPECTATOR MODE: Completely separate game setup
  // Uses the same game engine but all players are AI-controlled
  const startSpectatorGame = (diff) => {
    const numPlayers = 4;
    const mapSize = 800;
    
    // Pick 4 random species for the AI battle
    const allSpeciesIds = Object.keys(SPECIES);
    const shuffled = [...allSpeciesIds].sort(() => Math.random() - 0.5);
    const picks = shuffled.slice(0, numPlayers);
    
    // IMPORTANT: First species becomes "playerSpecies" for turn order only
    // The rest become "aiSpecies" - this keeps existing turn logic intact
    const firstSpecies = picks[0];
    const otherSpecies = picks.slice(1);
    
    setPlayerSpecies(firstSpecies);
    setAiSpecies(otherSpecies);
    
    // Use standard game setup - no changes to game logic
    const map = generateMap(mapSize, mapSize, 'open', numPlayers);
    const initialized = setupGame(map, firstSpecies, otherSpecies, 'open', diff);
    
    setBarriers([]);
    setViewportOffset({ x: 0, y: 0 });
    setZoomLevel(1);
    setTerritories(initialized);
    setCurrentPlayer(firstSpecies);
    setTurn(1);
    setGameOver(null);
    setMutations(Object.fromEntries(picks.map(s => [s, []])));
    setNutrientsCaptured(Object.fromEntries(picks.map(s => [s, 0])));
    setCurrentEvent(null);
    setUsedFreeExpand(false);
    setSupplyRoutes({});
    setIsSettingSupplyRoute(false);
    setMessage(`👁️ Spectating - ${SPECIES[firstSpecies]?.name}'s turn...`);
    setGamePhase('playing');
  };
  
  const startGame = (selectedSpecies) => {
    // Determine number of AI opponents based on map type
    const numAI = mapType === 'outbreak' ? 7 : 3;
    const mapSize = mapType === 'outbreak' ? 1600 : 800;
    
    // Pick random AI species (not the player's)
    const available = Object.keys(SPECIES).filter(s => s !== selectedSpecies);
    const shuffled = available.sort(() => Math.random() - 0.5);
    const aiPicks = shuffled.slice(0, numAI);
    
    setPlayerSpecies(selectedSpecies);
    setAiSpecies(aiPicks);
    
    const map = generateMap(mapSize, mapSize, mapType, numAI + 1);
    const initialized = setupGame(map, selectedSpecies, aiPicks, mapType, difficulty);
    
    // Store barriers for rendering
    setBarriers(map._barriers || []);
    
    // Reset viewport for outbreak mode
    if (mapType === 'outbreak' || mapType === 'open'){
      setViewportOffset({ x: 0, y: 0 }); // Start centered
      // On mobile, start zoomed out so player can see more of the map
      const mobile = window.innerWidth < 850 || 'ontouchstart' in window;
      setZoomLevel(mobile ? 0.35 : 0.75);
    } else {
      setViewportOffset({ x: 0, y: 0 });
      setZoomLevel(1);
    }
    
    setTerritories(initialized);
    setCurrentPlayer(selectedSpecies);
    setTurn(1);
    setGameOver(null);
    setMutations({ [selectedSpecies]: [], ...Object.fromEntries(aiPicks.map(s => [s, []])) });
    setNutrientsCaptured({ [selectedSpecies]: 0, ...Object.fromEntries(aiPicks.map(s => [s, 0])) });
    setCurrentEvent(null);
    setUsedFreeExpand(false);
    setSupplyRoutes({}); // Reset supply routes
    setIsSettingSupplyRoute(false);
    // Reset game stats
    setGameStats({
      attacksWon: 0,
      attacksLost: 0,
      territoriesCaptured: 0,
      maxTerritories: 0,
      enemiesEliminated: 0,
      peakBiomass: 0,
    });
    setMessage('Your turn - tap a colony');
    setGamePhase('playing');
  };
  
  // ============ SUPPLY LINE HELPERS ============
  
  // Check if a path exists between two territories through friendly territory
  const findPathBetween = useCallback((fromId, toId, owner, territoriesList) => {
    if (fromId === toId) return [];
    
    const visited = new Set();
    const queue = [[fromId]];
    
    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];
      
      if (current === toId) return path;
      if (visited.has(current)) continue;
      visited.add(current);
      
      const territory = territoriesList.find(t => t.id === current);
      if (!territory) continue;
      
      for (const neighborId of territory.neighbors) {
        const neighbor = territoriesList.find(t => t.id === neighborId);
        if (neighbor && neighbor.owner === owner && !visited.has(neighborId)) {
          queue.push([...path, neighborId]);
        }
      }
    }
    
    return null; // No path found
  }, []);
  
  // Get all valid supply route destinations from a source
  const getValidSupplyDestinations = useCallback((sourceId) => {
    const source = territories.find(t => t.id === sourceId);
    if (!source || source.owner !== playerSpecies) return [];
    
    const validDestinations = [];
    const playerTerritories = territories.filter(t => t.owner === playerSpecies && t.id !== sourceId);
    
    for (const dest of playerTerritories) {
      const path = findPathBetween(sourceId, dest.id, playerSpecies, territories);
      if (path) {
        validDestinations.push(dest.id);
      }
    }
    
    return validDestinations;
  }, [territories, playerSpecies, findPathBetween]);
  
  // Check if a supply route is still valid (path exists)
  const isSupplyRouteValid = useCallback((sourceId, destId) => {
    const source = territories.find(t => t.id === sourceId);
    const dest = territories.find(t => t.id === destId);
    if (!source || !dest || source.owner !== playerSpecies || dest.owner !== playerSpecies) {
      return false;
    }
    return findPathBetween(sourceId, destId, playerSpecies, territories) !== null;
  }, [territories, playerSpecies, findPathBetween]);
  
  // ============ GROWTH ============
  
 const calculateGrowth = useCallback((owner) => {
    const owned = territories.filter(t => t.owner === owner);
    
    // === DETERMINISTIC MODE (Early Exit) ===
    // If this is true, we return the simple math and STOP.
    if (combatMode === 'deterministic') {
      return owned.length * 2;
    }

    // === PROBABILISTIC MODE (Original Logic) ===
    // The code below only runs if we didn't return above.
    const ownerMutations = mutations[owner] || [];
    
    // Base growth
    const baseGrowthPerTerritory = mapType === 'outbreak' ? 1 : 2;
    let growth = owned.length * baseGrowthPerTerritory;
    
    // Diminishing returns (Outbreak mode)
    if (mapType === 'outbreak' && owned.length > 15) {
      const penalty = Math.floor((owned.length - 15) / 5);
      growth = Math.max(owned.length, growth - penalty);
    }
    
    // Rapid growth mutation
    if (ownerMutations.includes('rapid_growth')) {
      growth += owned.length;
    }
    
    // Penicillium passive
    if (owner === 'penicillium') {
      const terrainTypes = new Set(owned.map(t => t.terrain).filter(t => t !== 'normal'));
      growth += terrainTypes.size;
    }
    
    // Rhizopus passive
    if (owner === 'rhizopus') {
      const emptyAdjacent = new Set();
      owned.forEach(t => {
        t.neighbors.forEach(n => {
          const neighbor = territories.find(x => x.id === n);
          if (neighbor && neighbor.owner === null) {
            emptyAdjacent.add(n);
          }
        });
      });
      growth += emptyAdjacent.size;
    }
    
    // Terrain bonuses
    owned.forEach(t => {
      if (t.terrain === 'nutrient') growth += 2;
      if (t.terrain === 'symbiote') {
        const symbioteBonus = Math.floor(owned.length / 4);
        growth += symbioteBonus;
      }
    });
    
    // Network bonus
    const visited = new Set();
    let largestBlob = 0;
    owned.forEach(startTerr => {
      if (visited.has(startTerr.id)) return;
      let blobSize = 0;
      const toExplore = [startTerr.id];
      while (toExplore.length > 0) {
        const currentId = toExplore.pop();
        if (visited.has(currentId)) continue;
        visited.add(currentId);
        blobSize++;
        const current = territories.find(x => x.id === currentId);
        if (current) {
          current.neighbors.forEach(n => {
            const neighbor = territories.find(x => x.id === n);
            if (neighbor && neighbor.owner === owner && !visited.has(n)) {
              toExplore.push(n);
            }
          });
        }
      }
      largestBlob = Math.max(largestBlob, blobSize);
    });
    
    const networkBonus = Math.min(6, largestBlob);
    if (ownerMutations.includes('network')) {
      growth += networkBonus + 2;
    } else if (largestBlob >= 3) {
      growth += networkBonus;
    }
    
    // Event modifier
    if (currentEvent?.effect === 'growth') {
      growth += currentEvent.value;
    }
    
    return Math.max(0, growth);
  }, [territories, mutations, currentEvent, combatMode, mapType]);
  
  const distributeGrowth = useCallback((owner) => {
    let growth = calculateGrowth(owner);
    const owned = territories.filter(t => t.owner === owner);
    if (owned.length === 0 || growth === 0) return;
    
    // Stachybotrys passive: -1 growth for enemies adjacent to Stachybotrys territories
    if (combatMode !== 'deterministic' && owner !== 'stachybotrys') {
      const stachyTerritories = territories.filter(t => t.owner === 'stachybotrys');
      let adjacentToStachy = 0;
      owned.forEach(t => {
        const isAdjacentToStachy = t.neighbors.some(n => {
          const neighbor = territories.find(x => x.id === n);
          return neighbor && neighbor.owner === 'stachybotrys';
        });
        if (isAdjacentToStachy) adjacentToStachy++;
      });
      // Reduce growth by number of territories adjacent to Stachybotrys (capped)
      growth = Math.max(0, growth - Math.min(adjacentToStachy, 3));
    }
    
    if (growth === 0) return;
    
    setTerritories(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const perTerritory = Math.floor(growth / owned.length);
      const remainder = growth % owned.length;
      
      // Track redirected growth for supply routes
      const redirectedGrowth = {}; // { destId: amount }
      
      owned.forEach((t, i) => {
        const territory = updated.find(x => x.id === t.id);
        const growthAmount = perTerritory + (i < remainder ? 1 : 0);
        
        // Check if this territory has a supply route (only for player)
        if (owner === playerSpecies && supplyRoutes[t.id] !== undefined) {
          const destId = supplyRoutes[t.id];
          const dest = updated.find(x => x.id === destId);
          
          // Verify route is still valid (destination still owned by player)
          if (dest && dest.owner === playerSpecies) {
            // Check if path still exists
            const pathExists = findPathBetween(t.id, destId, playerSpecies, updated);
            if (pathExists) {
              // Redirect growth to destination
              redirectedGrowth[destId] = (redirectedGrowth[destId] || 0) + growthAmount;
            } else {
              // Path broken, growth stays at source
              territory.biomass += growthAmount;
            }
          } else {
            // Destination lost, growth stays at source
            territory.biomass += growthAmount;
          }
        } else {
          // No supply route, normal growth
          territory.biomass += growthAmount;
        }
      });
      
      // Apply redirected growth to destinations
      for (const [destId, amount] of Object.entries(redirectedGrowth)) {
        const dest = updated.find(x => x.id === parseInt(destId));
        if (dest) {
          dest.biomass += amount;
        }
      }
      
      return updated;
    });
    
    // Clean up broken supply routes after growth distribution
    if (owner === playerSpecies) {
      setSupplyRoutes(prev => {
        const validRoutes = {};
        for (const [sourceId, destId] of Object.entries(prev)) {
          if (isSupplyRouteValid(parseInt(sourceId), destId)) {
            validRoutes[sourceId] = destId;
          }
        }
        return validRoutes;
      });
    }
  }, [territories, calculateGrowth, playerSpecies, supplyRoutes, findPathBetween, isSupplyRouteValid]);
  
  // ============ EVENTS ============
  
  const triggerEvent = useCallback(() => {
    const event = EVENTS[Math.floor(Math.random() * EVENTS.length)];
    setCurrentEvent(event);
    setShowEvent(true);
    
    if (event.effect === 'plague') {
      setTerritories(prev => {
        const updated = JSON.parse(JSON.stringify(prev));
        updated.forEach(t => {
          // Candida is immune to plague
          if (t.owner && t.owner !== 'candida' && t.biomass <= event.value) {
            t.owner = null;
            t.biomass = 0;
          }
        });
        return updated;
      });
    }
    
    setTimeout(() => setShowEvent(false), 2000);
  }, []);
  
  // ============ MUTATIONS ============
  
  const checkMutationUnlock = useCallback((owner, newNutrients) => {
    const ownerMutations = mutations[owner] || [];
    const threshold = (ownerMutations.length + 1) * 2;
    
    if (newNutrients >= threshold) {
      // In spectator mode, ALL species auto-select (including "playerSpecies")
      // In normal mode, only player gets to choose
      if (owner === playerSpecies && !isSpectatorMode) {
        const available = MUTATIONS.filter(m => !ownerMutations.includes(m.id));
        const choices = [];
        while (choices.length < 3 && available.length > 0) {
          const idx = Math.floor(Math.random() * available.length);
          choices.push(available.splice(idx, 1)[0]);
        }
        if (choices.length > 0) {
          setMutationChoices(choices);
          setShowMutationPicker(true);
        }
      } else {
        const available = MUTATIONS.filter(m => !ownerMutations.includes(m.id));
        if (available.length > 0) {
          const pick = available[Math.floor(Math.random() * available.length)];
          setMutations(prev => ({ ...prev, [owner]: [...(prev[owner] || []), pick.id] }));
        }
      }
    }
  }, [mutations, playerSpecies, isSpectatorMode]);
  
  const selectMutation = (mutationId) => {
    setMutations(prev => ({ ...prev, [playerSpecies]: [...(prev[playerSpecies] || []), mutationId] }));
    setShowMutationPicker(false);
    setMutationChoices([]);
  };
  
  // ============ TERRITORY INTERACTION ============
  
  const getValidTargets = useCallback((fromTerritory) => {
    const validIds = new Set(fromTerritory.neighbors);
    
    // Spore launcher: can attack 2 tiles away
    if (fromTerritory.terrain === 'spore') {
      fromTerritory.neighbors.forEach(n => {
        const neighbor = territories.find(t => t.id === n);
        if (neighbor) {
          neighbor.neighbors.forEach(nn => {
            if (nn !== fromTerritory.id) validIds.add(nn);
          });
        }
      });
    }
    
    // Chain movement: find all friendly territories connected through hyphae network
    const friendlyReachable = new Set();
    const toExplore = [fromTerritory.id];
    const visited = new Set([fromTerritory.id]);
    
    while (toExplore.length > 0) {
      const currentId = toExplore.pop();
      const current = territories.find(t => t.id === currentId);
      if (!current) continue;
      
      current.neighbors.forEach(neighborId => {
        if (visited.has(neighborId)) return;
        visited.add(neighborId);
        
        const neighbor = territories.find(t => t.id === neighborId);
        if (neighbor && neighbor.owner === fromTerritory.owner) {
          friendlyReachable.add(neighborId);
          toExplore.push(neighborId);
        }
      });
    }
    
    // Add all friendly reachable territories to valid targets
    friendlyReachable.forEach(id => validIds.add(id));
    
    return Array.from(validIds);
  }, [territories]);
  
  // Check if target is reachable via chain (for reinforcement) vs adjacent (for attack)
  const isChainReinforcement = useCallback((fromTerritory, toTerritory) => {
    if (!fromTerritory || !toTerritory) return false;
    if (toTerritory.owner !== fromTerritory.owner) return false;
    
    // Check if directly adjacent
    if (fromTerritory.neighbors.includes(toTerritory.id)) return false;
    
    // Must be chain movement
    return true;
  }, []);
  
  const handleTerritoryClick = (territory) => {
    if (currentPlayer !== playerSpecies || gameOver || isInCombat || showMutationPicker) return;
    
    // Supply route setting mode
    if (isSettingSupplyRoute && selected !== null) {
      const validDests = getValidSupplyDestinations(selected);
      
      if (territory.id === selected) {
        // Clicking source again cancels
        setIsSettingSupplyRoute(false);
        setMessage('Supply route cancelled');
      } else if (validDests.includes(territory.id)) {
        // Set the supply route
        setSupplyRoutes(prev => ({ ...prev, [selected]: territory.id }));
        setIsSettingSupplyRoute(false);
        setSelected(null);
        setMessage(`Supply route set! Growth will flow to destination.`);
      } else if (territory.owner === playerSpecies) {
        setMessage('No path to that territory!');
      } else {
        setMessage('Must route to your own territory!');
      }
      return;
    }
    
    if (selected === null) {
      if (territory.owner === playerSpecies && territory.biomass > 1) {
        setSelected(territory.id);
        setMessage('Tap target, or tap again to deselect');
      } else if (territory.owner === playerSpecies) {
        setMessage('Need more than 1 biomass');
      }
    } else if (selected === territory.id) {
      setSelected(null);
      setIsSettingSupplyRoute(false);
      setMessage('Tap a colony to select');
    } else {
      const attacker = territories.find(t => t.id === selected);
      const validTargets = getValidTargets(attacker);
      
      if (!validTargets.includes(territory.id)) {
        setMessage('Target out of range!');
        return;
      }
      
      setTarget(territory.id);
      setMoveAmount(Math.max(1, attacker.biomass - 1));
      setShowMoveSelector(true);
    }
  };
  
  // ============ EXECUTE MOVES ============
  
  const executeMove = () => {
    const attacker = territories.find(t => t.id === selected);
    const targetTerritory = territories.find(t => t.id === target);
    
    if (targetTerritory.owner === null) {
      if (targetTerritory.terrain === 'nutrient') {
        const newCount = (nutrientsCaptured[playerSpecies] || 0) + 1;
        setNutrientsCaptured(prev => ({ ...prev, [playerSpecies]: newCount }));
        checkMutationUnlock(playerSpecies, newCount);
      }
      
      setTerritories(prev => {
        const updated = JSON.parse(JSON.stringify(prev));
        const from = updated.find(t => t.id === selected);
        const to = updated.find(t => t.id === target);
        from.biomass -= moveAmount;
        to.owner = playerSpecies;
        to.biomass = moveAmount;
        return updated;
      });
      setMessage(`Colonized with ${moveAmount}`);
      resetSelection();
    } else if (targetTerritory.owner === playerSpecies) {
      setTerritories(prev => {
        const updated = JSON.parse(JSON.stringify(prev));
        const from = updated.find(t => t.id === selected);
        const to = updated.find(t => t.id === target);
        from.biomass -= moveAmount;
        to.biomass += moveAmount;
        return updated;
      });
      setMessage(`Transferred ${moveAmount}`);
      resetSelection();
   } else {
      // === REPLACED COMBAT BLOCK ===
      setIsInCombat(true);
      setShowMoveSelector(false);
      
      let attackerTroops = moveAmount;
      const defenderSpeciesId = targetTerritory.owner;
      
      // Terrain reductions
      if (targetTerritory.terrain === 'toxic' || defenderSpeciesId === 'mucor') {
        attackerTroops = Math.max(1, attackerTroops - 1);
      }
      if ((mutations[defenderSpeciesId] || []).includes('toxic_skin')) {
        attackerTroops = Math.max(1, attackerTroops - 1);
      }
      
      // CHECK MODE
      if (combatMode === 'deterministic') {
         resolveDeterministicCombat(selected, target, attackerTroops, targetTerritory.biomass, playerSpecies, defenderSpeciesId, targetTerritory.terrain);
      } else {
         setCombatLog([]);
         runCombat(selected, target, attackerTroops, targetTerritory.biomass, playerSpecies, defenderSpeciesId, targetTerritory.terrain);
      }
    }
  };
  // --- PASTE THIS NEW FUNCTION BEFORE runCombat ---
  const resolveDeterministicCombat = (attackerId, defenderId, attackerTroops, defenderTroops, attackerSpeciesId, defenderSpeciesId, defenderTerrain) => {
    // 1. Gather Data
    const attackerMuts = mutations[attackerSpeciesId] || [];
    const defenderMuts = mutations[defenderSpeciesId] || [];
    const defenderTerritory = territories.find(t => t.id === defenderId);
    
    // Calculate Pincer
    const pincerCount = defenderTerritory ? defenderTerritory.neighbors.filter(n => {
      const neighbor = territories.find(t => t.id === n);
      return neighbor && neighbor.owner === attackerSpeciesId && neighbor.id !== attackerId;
    }).length : 0;

    // 2. Run Math
    const prediction = calculateDetailedWinProbability(
      attackerTroops, defenderTroops, attackerSpeciesId, defenderSpeciesId,
      attackerMuts, defenderMuts, defenderTerrain, pincerCount,
      true // isDeterministic = true
    );

    const { deterministicResult, survivors } = prediction;

    // 3. Apply Outcome
    setTerritories(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const attacker = updated.find(t => t.id === attackerId);
      const defender = updated.find(t => t.id === defenderId);

      // Attacker always spends troops to launch
      attacker.biomass -= moveAmount; 

      if (deterministicResult === 'win') {
        // Cap survivors at amount sent (Physics check)
        let finalSurvivors = Math.min(survivors, attackerTroops);
        
        // Bonuses that spawn NEW biomass
        if (attackerMuts.includes('efficient')) finalSurvivors += 2;
        if (attackerSpeciesId === 'cordyceps') finalSurvivors += 2;
        
        // Nutrients
        if (defender.terrain === 'nutrient') {
          const newCount = (nutrientsCaptured[attackerSpeciesId] || 0) + 1;
          setNutrientsCaptured(p => ({ ...p, [attackerSpeciesId]: newCount }));
          if (attackerSpeciesId === playerSpecies) checkMutationUnlock(attackerSpeciesId, newCount);
        }

        defender.owner = attackerSpeciesId;
        defender.biomass = Math.max(1, finalSurvivors);
        
        // Stats
        if (attackerSpeciesId === playerSpecies) {
          setGameStats(s => ({ ...s, attacksWon: s.attacksWon + 1, territoriesCaptured: s.territoriesCaptured + 1 }));
        }
        setMessage(`Victory! ${finalSurvivors} units landed.`);
      } 
      else if (deterministicResult === 'draw') {
        defender.owner = null;
        defender.biomass = 0;
        if (attackerSpeciesId === playerSpecies) setGameStats(s => ({ ...s, attacksLost: s.attacksLost + 1 }));
        setMessage('Mutual Destruction! Zone neutralized.');
      } 
      else {
        // Loss - Defender survives
        let defPower = prediction.defenderDice; 
        let remaining = defPower - prediction.attackerEffective;
        defender.biomass = Math.max(1, remaining);
        if (attackerSpeciesId === playerSpecies) setGameStats(s => ({ ...s, attacksLost: s.attacksLost + 1 }));
        setMessage('Attack Repelled!');
      }
      return updated;
    });
    
    setIsInCombat(false);
    resetSelection();
  };
  
  const runCombat = (attackerId, defenderId, attackerTroops, defenderTroops, attackerSpeciesId, defenderSpeciesId, defenderTerrain) => {
    let attLeft = attackerTroops;
    let defLeft = defenderTroops;
    const logs = [];
    
    const attackerMuts = mutations[attackerSpeciesId] || [];
    const defenderMuts = mutations[defenderSpeciesId] || [];
    
    // Calculate pincer bonus
    const defenderTerritory = territories.find(t => t.id === defenderId);
    const pincerCount = defenderTerritory ? defenderTerritory.neighbors.filter(n => {
      const neighbor = territories.find(t => t.id === n);
      return neighbor && neighbor.owner === attackerSpeciesId && neighbor.id !== attackerId;
    }).length : 0;
    
    // Dice counts
    let attackerDice = attackerSpeciesId === 'aspergillus' ? 4 : 3;
    // Pincer bonus
    if (pincerCount >= 4) attackerDice += 2;
    else if (pincerCount >= 2) attackerDice += 1;
    
    let defenderDice = 2;
    if (defenderTerrain === 'fortified') defenderDice = 3;
    if (defenderSpeciesId === 'candida') defenderDice += 1;
    if (defenderMuts.includes('thick_walls')) defenderDice += 1;
    
    while (attLeft > 0 && defLeft > 0) {
      const result = resolveCombatRound(attLeft, defLeft, attackerDice, defenderDice);
      
      if (attackerMuts.includes('spore_burst') && result.defenderLosses > 0) {
        result.defenderLosses += 1;
      }
      
      attLeft -= result.attackerLosses;
      defLeft -= Math.min(defLeft, result.defenderLosses);
      
      logs.push({
        attackDice: result.attackDice,
        defenseDice: result.defenseDice,
        attackerLosses: result.attackerLosses,
        defenderLosses: result.defenderLosses,
        attackerRemaining: attLeft,
        defenderRemaining: defLeft,
      });
    }
    
    setCombatLog(logs);
    
    const finalAttLeft = attLeft;
    const finalDefLeft = defLeft;
    
    setTimeout(() => {
      setTerritories(prev => {
        const updated = JSON.parse(JSON.stringify(prev));
        const attacker = updated.find(t => t.id === attackerId);
        const defender = updated.find(t => t.id === defenderId);
        
        if (finalDefLeft <= 0) {
          if (defender.terrain === 'nutrient') {
            const newCount = (nutrientsCaptured[attackerSpeciesId] || 0) + 1;
            setNutrientsCaptured(p => ({ ...p, [attackerSpeciesId]: newCount }));
            if (attackerSpeciesId === playerSpecies) {
              checkMutationUnlock(attackerSpeciesId, newCount);
            }
          }
          
          let survivors = finalAttLeft;
          if (attackerMuts.includes('efficient')) survivors += 2;
          
          // Cordyceps passive: +2 biomass on conquest
          if (attackerSpeciesId === 'cordyceps') survivors += 2;
          
          // Track stats for player victories
          if (attackerSpeciesId === playerSpecies) {
            setGameStats(s => ({
              ...s,
              attacksWon: s.attacksWon + 1,
              territoriesCaptured: s.territoriesCaptured + 1,
            }));
          }
          
          defender.owner = attackerSpeciesId;
          defender.biomass = Math.max(1, survivors);
          attacker.biomass -= moveAmount;
          setMessage(`Victory! Captured with ${survivors}`);
        } else {
          // Track failed attacks
          if (attackerSpeciesId === playerSpecies) {
            setGameStats(s => ({ ...s, attacksLost: s.attacksLost + 1 }));
          }
          attacker.biomass -= (moveAmount - Math.max(0, finalAttLeft));
          defender.biomass = finalDefLeft;
          setMessage(`Attack failed!`);
        }
        
        return updated;
      });
      
      setIsInCombat(false);
      resetSelection();
    }, logs.length * 250 + 400);
  };
  
  const resetSelection = () => {
    setSelected(null);
    setTarget(null);
    setShowMoveSelector(false);
    setMoveAmount(1);
  };
  
  // ============ AI HELPER FUNCTIONS ============
  
  // Find territories that, if captured, would split an enemy's network
  const findChainBreakingTargets = useCallback((simulated, aiSpeciesId, targetOwner) => {
    const targetTerritories = simulated.filter(t => t.owner === targetOwner);
    if (targetTerritories.length < 3) return []; // Not worth analyzing small networks
    
    const breakingTargets = [];
    
    for (const candidate of targetTerritories) {
      // Check if removing this territory would split the network
      const remaining = targetTerritories.filter(t => t.id !== candidate.id);
      if (remaining.length === 0) continue;
      
      // BFS from first remaining territory
      const visited = new Set([remaining[0].id]);
      const toExplore = [remaining[0].id];
      
      while (toExplore.length > 0) {
        const currentId = toExplore.shift();
        const current = simulated.find(t => t.id === currentId);
        if (!current) continue;
        
        for (const neighborId of current.neighbors) {
          if (visited.has(neighborId)) continue;
          const neighbor = simulated.find(t => t.id === neighborId);
          if (neighbor && neighbor.owner === targetOwner && neighbor.id !== candidate.id) {
            visited.add(neighborId);
            toExplore.push(neighborId);
          }
        }
      }
      
      // If we didn't visit all remaining territories, this is a chain-breaking target
      if (visited.size < remaining.length) {
        // Check if AI can attack this territory
        const canAttack = candidate.neighbors.some(n => {
          const neighbor = simulated.find(t => t.id === n);
          return neighbor && neighbor.owner === aiSpeciesId;
        });
        
        if (canAttack) {
          breakingTargets.push({
            territory: candidate,
            splitSize: remaining.length - visited.size,
          });
        }
      }
    }
    
    return breakingTargets.sort((a, b) => b.splitSize - a.splitSize);
  }, []);
  
  // Find weak territories to mop up after a breakthrough
  const findMopUpTargets = useCallback((simulated, aiSpeciesId, fromTerritory, maxBiomass = 2) => {
    const targets = [];
    
    for (const neighborId of fromTerritory.neighbors) {
      const neighbor = simulated.find(t => t.id === neighborId);
      if (neighbor && 
          neighbor.owner && 
          neighbor.owner !== aiSpeciesId && 
          neighbor.biomass <= maxBiomass) {
        targets.push(neighbor);
      }
    }
    
    return targets.sort((a, b) => a.biomass - b.biomass);
  }, []);
  
  // Check if player has recently spread thin (punish window)
  const detectPunishWindow = useCallback((simulated, targetSpecies, aiSpeciesId) => {
    const targetTerritories = simulated.filter(t => t.owner === targetSpecies);
    
    let vulnerableCount = 0;
    let totalAdjacent = 0;
    
    for (const pt of targetTerritories) {
      const aiCanReach = pt.neighbors.some(n => {
        const neighbor = simulated.find(t => t.id === n);
        return neighbor && neighbor.owner === aiSpeciesId;
      });
      
      if (aiCanReach) {
        totalAdjacent++;
        if (pt.biomass <= 2) vulnerableCount++;
      }
    }
    
    return totalAdjacent > 0 && (vulnerableCount / totalAdjacent) > 0.4;
  }, []);

  // ============ AI TURN ============
  
  const runAITurn = useCallback((aiSpeciesId) => {
    setCurrentPlayer(aiSpeciesId);
    setMessage(`${SPECIES[aiSpeciesId]?.name} turn...`);
    
    const diffSettings = DIFFICULTY[difficulty] || DIFFICULTY.normal;
    
   setTimeout(() => {
      // Only distribute growth if it's a standard AI. 
      // If it's the "Player" species in Spectator mode, they already got growth from nextPlayer()
      if (aiSpeciesId !== playerSpecies) {
        distributeGrowth(aiSpeciesId);
      }
      
      setTimeout(() => {
        const actions = [];
        const simulated = JSON.parse(JSON.stringify(territories));
        const aiMuts = mutations[aiSpeciesId] || [];
        
        // Get valid targets including spore launcher range
        const getAIValidTargets = (territory) => {
          let validTargets = [...territory.neighbors];
          if (territory.terrain === 'spore') {
            territory.neighbors.forEach(n => {
              const neighbor = simulated.find(t => t.id === n);
              if (neighbor) {
                neighbor.neighbors.forEach(nn => {
                  if (nn !== territory.id && !validTargets.includes(nn)) validTargets.push(nn);
                });
              }
            });
          }
          return validTargets;
        };
        
        // ============ HARD/INSANE: Check for punish window ============
        const inPunishWindow = diffSettings.usePunishWindow && detectPunishWindow(simulated, playerSpecies, aiSpeciesId);
        
        // ============ HARD/INSANE: Chain-breaking targets ============
        let chainBreakingTargets = [];
        if (diffSettings.useChainBreaking) {
          const enemies = [...new Set(simulated.filter(t => t.owner && t.owner !== aiSpeciesId).map(t => t.owner))];
          for (const enemy of enemies) {
            const breaks = findChainBreakingTargets(simulated, aiSpeciesId, enemy);
            chainBreakingTargets.push(...breaks);
          }
        }
        
        // ============ PRIORITY 1: Expand to empty ============
        // More expansions on outbreak maps
        const outbreakExpandBonus = mapType === 'outbreak' ? 3 : 0;
        for (let i = 0; i < diffSettings.maxExpands + outbreakExpandBonus; i++) {
          const expandable = simulated
            .filter(t => t.owner === aiSpeciesId && t.biomass > 2)
            .sort((a, b) => b.biomass - a.biomass);
          
          let found = false;
          for (const territory of expandable) {
            const validTargets = getAIValidTargets(territory);
            
            let emptyTargets = validTargets
              .map(n => simulated.find(t => t.id === n))
              .filter(t => t && t.owner === null);
            
            // Prioritize nutrients if setting enabled
            if (diffSettings.prioritizeNutrients) {
              emptyTargets.sort((a, b) => {
                const aIsNutrient = a.terrain === 'nutrient' ? 1 : 0;
                const bIsNutrient = b.terrain === 'nutrient' ? 1 : 0;
                return bIsNutrient - aIsNutrient;
              });
            }
            
            const emptyNeighbor = emptyTargets[0];
            
            if (emptyNeighbor) {
              const amount = Math.min(territory.biomass - 1, 3);
              
              if (emptyNeighbor.terrain === 'nutrient') {
                const newCount = (nutrientsCaptured[aiSpeciesId] || 0) + 1;
                setNutrientsCaptured(p => ({ ...p, [aiSpeciesId]: newCount }));
                checkMutationUnlock(aiSpeciesId, newCount);
              }
              
              actions.push({ type: 'expand', from: territory.id, to: emptyNeighbor.id, amount, message: `${SPECIES[aiSpeciesId]?.name} expands` });
              territory.biomass -= amount;
              emptyNeighbor.owner = aiSpeciesId;
              emptyNeighbor.biomass = amount;
              found = true;
              break;
            }
          }
          if (!found) break;
        }
        
        // ============ PRIORITY 2: Attack ============
        let attackCount = 0;
        // More attacks allowed on outbreak maps to keep pace with larger territory counts
        const outbreakBonus = mapType === 'outbreak' ? 3 : 0;
        const maxAttacks = (inPunishWindow ? diffSettings.maxAttacks + 2 : diffSettings.maxAttacks) + outbreakBonus;
        
        while (attackCount < maxAttacks) {
          const attackers = simulated
            .filter(t => t.owner === aiSpeciesId && t.biomass > 2)
            .sort((a, b) => b.biomass - a.biomass);
          
          let found = false;
          
          for (const attacker of attackers) {
            const validTargets = getAIValidTargets(attacker);
            
            let enemies = validTargets
              .map(n => simulated.find(t => t.id === n))
              .filter(t => t && t.owner && t.owner !== aiSpeciesId);
            
            // ============ HARD/INSANE: Prioritize chain-breaking targets ============
            if (diffSettings.useChainBreaking && chainBreakingTargets.length > 0) {
              enemies.sort((a, b) => {
                const aIsChainBreak = chainBreakingTargets.some(cb => cb.territory.id === a.id) ? 1 : 0;
                const bIsChainBreak = chainBreakingTargets.some(cb => cb.territory.id === b.id) ? 1 : 0;
                if (bIsChainBreak !== aIsChainBreak) return bIsChainBreak - aIsChainBreak;
                
                const matchupA = getSpeciesMatchupModifier(aiSpeciesId, a.owner);
                const matchupB = getSpeciesMatchupModifier(aiSpeciesId, b.owner);
                return matchupB - matchupA;
              });
            } else {
              enemies.sort((a, b) => {
                const matchupA = getSpeciesMatchupModifier(aiSpeciesId, a.owner);
                const matchupB = getSpeciesMatchupModifier(aiSpeciesId, b.owner);
                return matchupB - matchupA;
              });
            }
            
            for (const target of enemies) {
              const pincerCount = target.neighbors.filter(n => {
                const neighbor = simulated.find(t => t.id === n);
                return neighbor && neighbor.owner === aiSpeciesId && neighbor.id !== attacker.id;
              }).length;
              
              const winProb = calculateWinProbability(attacker.biomass, target.biomass, aiSpeciesId, target.owner, target.terrain, pincerCount, combatMode === 'deterministic');
              
              const threshold = inPunishWindow ? Math.max(0.30, diffSettings.minWinProb - 0.10) : diffSettings.minWinProb;
              
              if (winProb >= threshold) {
                let attLeft = attacker.biomass - 1;
                if (target.terrain === 'toxic' || target.owner === 'mucor') attLeft = Math.max(1, attLeft - 1);
                
                const targetMuts = mutations[target.owner] || [];
                if (targetMuts.includes('toxic_skin')) attLeft = Math.max(1, attLeft - 1);
                
                let defLeft = target.biomass;
                let atkDice = aiSpeciesId === 'aspergillus' ? 4 : 3;
                if (pincerCount >= 4) atkDice += 2;
                else if (pincerCount >= 2) atkDice += 1;
                
                let defDice = target.terrain === 'fortified' ? 3 : 2;
                if (target.owner === 'candida') defDice += 1;
                if (targetMuts.includes('thick_walls')) defDice += 1;
                
                while (attLeft > 0 && defLeft > 0) {
                  const result = resolveCombatRound(attLeft, defLeft, atkDice, defDice);
                  if (aiMuts.includes('spore_burst') && result.defenderLosses > 0) result.defenderLosses += 1;
                  attLeft -= result.attackerLosses;
                  defLeft -= Math.min(defLeft, result.defenderLosses);
                }
                
                const won = defLeft <= 0;
                actions.push({ type: 'attack', from: attacker.id, to: target.id, won, remaining: won ? attLeft : defLeft, message: won ? `${SPECIES[aiSpeciesId]?.name} captures!` : `${SPECIES[aiSpeciesId]?.name} repelled!` });
                
                if (won) {
                  if (target.terrain === 'nutrient') {
                    const newCount = (nutrientsCaptured[aiSpeciesId] || 0) + 1;
                    setNutrientsCaptured(p => ({ ...p, [aiSpeciesId]: newCount }));
                    checkMutationUnlock(aiSpeciesId, newCount);
                  }
                  let survivors = attLeft;
                  if (aiMuts.includes('efficient')) survivors += 2;
                  
                  target.owner = aiSpeciesId;
                  target.biomass = Math.max(1, survivors);
                  attacker.biomass = 1;
                  
                  // ============ HARD/INSANE: Mop-up chain ============
                  if (diffSettings.useMopUp && survivors >= 2) {
                    let mopUpTarget = simulated.find(t => t.id === target.id);
                    let mopUpAttempts = 0;
                    const maxMopUps = 3;
                    
                    while (mopUpAttempts < maxMopUps && mopUpTarget && mopUpTarget.biomass >= 2) {
                      const weakTargets = findMopUpTargets(simulated, aiSpeciesId, mopUpTarget, 2);
                      
                      if (weakTargets.length === 0) break;
                      
                      const nextTarget = weakTargets[0];
                      const mopPincer = nextTarget.neighbors.filter(n => {
                        const neighbor = simulated.find(t => t.id === n);
                        return neighbor && neighbor.owner === aiSpeciesId && neighbor.id !== mopUpTarget.id;
                      }).length;
                      
                      const mopWinProb = calculateWinProbability(mopUpTarget.biomass, nextTarget.biomass, aiSpeciesId, nextTarget.owner, nextTarget.terrain, mopPincer, combatMode === 'deterministic');
                      
                      if (mopWinProb < 0.60) break;
                      
                      let mopAttLeft = mopUpTarget.biomass - 1;
                      if (nextTarget.terrain === 'toxic' || nextTarget.owner === 'mucor') mopAttLeft = Math.max(1, mopAttLeft - 1);
                      
                      const mopDefMuts = mutations[nextTarget.owner] || [];
                      if (mopDefMuts.includes('toxic_skin')) mopAttLeft = Math.max(1, mopAttLeft - 1);
                      
                      let mopDefLeft = nextTarget.biomass;
                      let mopAtkDice = aiSpeciesId === 'aspergillus' ? 4 : 3;
                      if (mopPincer >= 4) mopAtkDice += 2;
                      else if (mopPincer >= 2) mopAtkDice += 1;
                      
                      let mopDefDice = nextTarget.terrain === 'fortified' ? 3 : 2;
                      if (nextTarget.owner === 'candida') mopDefDice += 1;
                      if (mopDefMuts.includes('thick_walls')) mopDefDice += 1;
                      
                      while (mopAttLeft > 0 && mopDefLeft > 0) {
                        const result = resolveCombatRound(mopAttLeft, mopDefLeft, mopAtkDice, mopDefDice);
                        if (aiMuts.includes('spore_burst') && result.defenderLosses > 0) result.defenderLosses += 1;
                        mopAttLeft -= result.attackerLosses;
                        mopDefLeft -= Math.min(mopDefLeft, result.defenderLosses);
                      }
                      
                      const mopWon = mopDefLeft <= 0;
                      actions.push({ 
                        type: 'attack', 
                        from: mopUpTarget.id, 
                        to: nextTarget.id, 
                        won: mopWon, 
                        remaining: mopWon ? mopAttLeft : mopDefLeft, 
                        message: mopWon ? `${SPECIES[aiSpeciesId]?.name} mops up!` : `${SPECIES[aiSpeciesId]?.name} stopped!` 
                      });
                      
                      if (mopWon) {
                        if (nextTarget.terrain === 'nutrient') {
                          const newCount = (nutrientsCaptured[aiSpeciesId] || 0) + 1;
                          setNutrientsCaptured(p => ({ ...p, [aiSpeciesId]: newCount }));
                          checkMutationUnlock(aiSpeciesId, newCount);
                        }
                        let mopSurvivors = mopAttLeft;
                        if (aiMuts.includes('efficient')) mopSurvivors += 2;
                        
                        nextTarget.owner = aiSpeciesId;
                        nextTarget.biomass = Math.max(1, mopSurvivors);
                        mopUpTarget.biomass = 1;
                        
                        mopUpTarget = nextTarget;
                      } else {
                        mopUpTarget.biomass = 1;
                        break;
                      }
                      
                      mopUpAttempts++;
                    }
                  }
                  
                } else {
                  attacker.biomass = 1;
                  target.biomass = defLeft;
                }
                found = true;
                attackCount++;
                break;
              }
            }
            if (found) break;
          }
          if (!found) break;
        }
        
        // ============ PRIORITY 3: Reinforce via chain movement ============
        const aiTerritories = simulated.filter(t => t.owner === aiSpeciesId);
        const frontLine = aiTerritories.filter(t => 
          t.neighbors.some(n => {
            const neighbor = simulated.find(x => x.id === n);
            return neighbor && neighbor.owner && neighbor.owner !== aiSpeciesId;
          })
        );
        
        const backLine = aiTerritories.filter(t => t.biomass > 3 && !frontLine.includes(t));
        
        for (const source of backLine) {
          const visited = new Set([source.id]);
          const toExplore = [source.id];
          let targetFront = null;
          
          while (toExplore.length > 0 && !targetFront) {
            const currentId = toExplore.shift();
            const current = simulated.find(t => t.id === currentId);
            if (!current) continue;
            
            for (const neighborId of current.neighbors) {
              if (visited.has(neighborId)) continue;
              visited.add(neighborId);
              
              const neighbor = simulated.find(t => t.id === neighborId);
              if (neighbor && neighbor.owner === aiSpeciesId) {
                if (frontLine.includes(neighbor)) {
                  targetFront = neighbor;
                  break;
                }
                toExplore.push(neighborId);
              }
            }
          }
          
          if (targetFront && source.biomass > 2) {
            const transfer = source.biomass - 1;
            actions.push({ type: 'reinforce', from: source.id, to: targetFront.id, amount: transfer, message: `${SPECIES[aiSpeciesId]?.name} reinforces` });
            source.biomass = 1;
            targetFront.biomass += transfer;
          }
        }
        
        // Execute actions
        const executeActions = (index) => {
          if (index >= actions.length) {
            setAiAction(null);
            nextPlayer(aiSpeciesId);
            return;
          }
          
          const action = actions[index];
          setAiAction(action);
          setMessage(action.message);
          
          setTerritories(prev => {
            const updated = JSON.parse(JSON.stringify(prev));
            const from = updated.find(t => t.id === action.from);
            const to = updated.find(t => t.id === action.to);
            
            if (action.type === 'expand') {
              from.biomass = Math.max(1, from.biomass - action.amount);
              to.owner = aiSpeciesId;
              to.biomass = Math.max(1, action.amount);
            } else if (action.type === 'attack') {
              if (action.won) {
                to.owner = aiSpeciesId;
                to.biomass = Math.max(1, action.remaining);
                from.biomass = 1;
              } else {
                from.biomass = 1;
                to.biomass = Math.max(1, action.remaining);
              }
            } else if (action.type === 'reinforce') {
              from.biomass = 1;
              to.biomass += action.amount;
            }
            return updated;
          });
          
          setTimeout(() => executeActions(index + 1), 400);
        };
        
        if (actions.length === 0) {
          nextPlayer(aiSpeciesId);
        } else {
          executeActions(0);
        }
      }, 300);
    }, 300);
  }, [territories, mutations, nutrientsCaptured, distributeGrowth, checkMutationUnlock, difficulty, playerSpecies, findChainBreakingTargets, findMopUpTargets, detectPunishWindow]);
  
  const nextPlayer = useCallback((currentSpecies) => {
    const allPlayers = [playerSpecies, ...aiSpecies];
    const alive = allPlayers.filter(s => territories.some(t => t.owner === s));
    
    if (alive.length <= 1) {
      // Game over
      return;
    }
    
    const currentIndex = allPlayers.indexOf(currentSpecies);
    let nextIndex = (currentIndex + 1) % allPlayers.length;
    
    // Find next alive player
    while (!alive.includes(allPlayers[nextIndex])) {
      nextIndex = (nextIndex + 1) % allPlayers.length;
    }
    
    const nextSpecies = allPlayers[nextIndex];
    
    // New round?
    if (nextSpecies === playerSpecies) {
      setTurn(t => t + 1);
      setUsedFreeExpand(false);
      
      // Event every 5 turns
      if ((turn + 1) % 5 === 0) {
        triggerEvent();
      }
      
      distributeGrowth(playerSpecies);
      setCurrentPlayer(playerSpecies);
      // Show different message in spectator mode
      if (isSpectatorMode) {
        setMessage(`👁️ Spectating - ${SPECIES[playerSpecies]?.name}'s turn...`);
      } else {
        setMessage('Your turn - tap a colony');
      }
    } else {
      setTimeout(() => runAITurn(nextSpecies), 500);
    }
  }, [playerSpecies, aiSpecies, territories, turn, distributeGrowth, triggerEvent, runAITurn, isSpectatorMode]);
  
  const endTurn = () => {
    if (currentPlayer !== playerSpecies || isInCombat || showMutationPicker) return;
    resetSelection();
    
    if (aiSpecies.length > 0) {
      const firstAliveAI = aiSpecies.find(s => territories.some(t => t.owner === s));
      if (firstAliveAI) {
        runAITurn(firstAliveAI);
      } else {
        // All AI dead - player wins
        setGameOver({ winner: playerSpecies });
      }
    }
  };
  
  // ============ VICTORY CHECK ============
  
  // Track max territories and peak biomass each turn
  useEffect(() => {
    if (territories.length === 0 || gamePhase !== 'playing' || isSpectatorMode) return;
    
    const playerTerritories = territories.filter(t => t.owner === playerSpecies);
    const playerBiomass = playerTerritories.reduce((sum, t) => sum + t.biomass, 0);
    
    setGameStats(s => ({
      ...s,
      maxTerritories: Math.max(s.maxTerritories, playerTerritories.length),
      peakBiomass: Math.max(s.peakBiomass, playerBiomass),
    }));
  }, [territories, playerSpecies, gamePhase, isSpectatorMode]);
  
  // Track eliminated enemies
  const [previousAlive, setPreviousAlive] = useState([]);
  
  useEffect(() => {
    if (territories.length === 0 || gamePhase !== 'playing') return;
    
    const allPlayers = [playerSpecies, ...aiSpecies];
    const alive = allPlayers.filter(s => territories.some(t => t.owner === s));
    
    // Check for newly eliminated players
    if (!isSpectatorMode && previousAlive.length > 0) {
      const eliminated = previousAlive.filter(s => !alive.includes(s) && s !== playerSpecies);
      if (eliminated.length > 0) {
        setGameStats(s => ({
          ...s,
          enemiesEliminated: s.enemiesEliminated + eliminated.length,
        }));
      }
    }
    setPreviousAlive(alive);
    
    // Game ends when only 1 player remains
    if (alive.length === 1) {
      const won = alive[0] === playerSpecies;
      if (!isSpectatorMode) {
        saveCareerStats(won);
      }
      setGameOver({ winner: alive[0] });
    } 
    // In normal mode (not spectator), also end if player is eliminated
    else if (!isSpectatorMode && !alive.includes(playerSpecies)) {
      saveCareerStats(false);
      setGameOver({ winner: alive[0] || 'AI' });
    }
  }, [territories, playerSpecies, aiSpecies, gamePhase, isSpectatorMode, previousAlive, saveCareerStats]);
  
  // ============ SPECTATOR AUTO-TURN (FIXED) ============
  useEffect(() => {
    // Only run in spectator mode
    if (!isSpectatorMode) return;
    // Only during gameplay
    if (gamePhase !== 'playing') return;
    // Don't run if game is over
    if (gameOver) return;
    // Only trigger when it's the "player" species turn
    if (currentPlayer !== playerSpecies) return;
    
    // --- THE FIX: PREVENT LOOPING ---
    // If we already triggered the AI for this specific turn/player combo, stop.
    // We combine turn + player to ensure it runs every new round.
    const turnKey = `${turn}-${currentPlayer}`;
    if (lastSpectatorTrigger.current === turnKey) return;
    
    // Lock it so it doesn't fire again on re-renders
    lastSpectatorTrigger.current = turnKey;

    // Delay then run AI for the first species
    const timer = setTimeout(() => {
      runAITurn(playerSpecies);
    }, 600);
    
    return () => clearTimeout(timer);
  }, [isSpectatorMode, gamePhase, gameOver, currentPlayer, playerSpecies, runAITurn, turn]);
  
  // ============ STATS ============
  
  const stats = useMemo(() => {
    const allPlayers = [playerSpecies, ...aiSpecies];
    return allPlayers.map(species => ({
      species,
      territories: territories.filter(t => t.owner === species).length,
      biomass: territories.filter(t => t.owner === species).reduce((sum, t) => sum + t.biomass, 0),
      growth: calculateGrowth(species),
    })).filter(s => s.territories > 0);
  }, [territories, playerSpecies, aiSpecies, calculateGrowth]);
  
  const selectedTerritory = selected !== null ? territories.find(t => t.id === selected) : null;
  const maxMove = selectedTerritory ? selectedTerritory.biomass - 1 : 0;
  const validTargets = selectedTerritory ? getValidTargets(selectedTerritory) : [];
  
 // ============ MENU SCREEN ============
  
  if (gamePhase === 'menu') {
    return (
      <div 
        className="flex flex-col items-center justify-center min-h-screen text-gray-100 p-4 relative overflow-hidden"
        style={{
          backgroundImage: `url(${UI_IMAGES.menuBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#1a1a2e', // Fallback color
        }}
      >
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />
        
        <div className="relative z-10 flex flex-col items-center">
          {/* Hero Dish - POSITIONING CONTROLS:
              - mt-X = push down (mt-0, mt-4, mt-8, mt-12...)
              - -mt-X = push UP / off screen (-mt-10, -mt-20, -mt-32...)
              - w-XX h-XX = size (w-64=256px, w-80=320px, w-96=384px)
          */}
          <div className="-mt-16 mb-2 relative">
            <div className="absolute inset-0 bg-teal-500/30 blur-2xl rounded-full scale-75" />
            <img 
              src={UI_IMAGES.heroDish} 
              alt="Fungal Colony" 
              className="w-72 h-72 object-contain relative"
              style={{ 
                filter: 'drop-shadow(0 8px 32px rgba(0, 0, 0, 0.5)) drop-shadow(0 0 60px rgba(0, 180, 160, 0.2))',
              }}
            />
          </div>

          {/* Title Image - width controls size, height auto-scales
              Original: 1390x278, so ~5:1 ratio
              max-w-md = 448px, max-w-lg = 512px, max-w-xl = 576px
          */}
          <img 
            src={UI_IMAGES.title} 
            alt="FUNGALOID" 
            className="w-full max-w-md mb-2 drop-shadow-lg"
            style={{
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.5))',
            }}
          />
          <p className="text-gray-300 mb-8 text-lg tracking-widest uppercase font-medium drop-shadow-md">Petri Dish Warfare</p>
          
          {/* Mode Buttons - Horizontal Layout
              w-28 = 112px, w-32 = 128px, w-36 = 144px, w-40 = 160px
          */}
          <div className="flex gap-6 items-end">
            {[
              { id: 'open', img: UI_IMAGES.buttons.openField, label: 'Open Field' },
              { id: 'outbreak', img: UI_IMAGES.buttons.chokepoint, label: 'Outbreak' },
              { id: 'spectate', img: UI_IMAGES.buttons.ring, label: '👁️ Spectate' }
            ].map(mode => (
              <button 
                key={mode.id}
                onClick={() => {
                  if (mode.id === 'spectate') {
                    // Spectator mode: set flag, use open map
                    setIsSpectatorMode(true);
                    setMapType('open');
                  } else {
                    // Normal modes: ALWAYS reset spectator flag
                    setIsSpectatorMode(false);
                    setMapType(mode.id);
                  }
                  setGamePhase('difficulty');
                }} 
                className="group flex flex-col items-center transition-all duration-300 hover:scale-105"
              >
                <div className="relative">
                  {/* Glow effect behind button - visible on hover */}
                  <div 
                    className="absolute inset-0 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      background: mode.id === 'spectate' 
                        ? 'radial-gradient(circle, rgba(168, 85, 247, 0.6) 0%, rgba(168, 85, 247, 0) 70%)'
                        : 'radial-gradient(circle, rgba(56, 189, 248, 0.6) 0%, rgba(56, 189, 248, 0) 70%)',
                      transform: 'scale(1.3)',
                      filter: 'blur(12px)',
                    }}
                  />
                  <img 
                    src={mode.img} 
                    alt={mode.label}
                    className="w-32 h-32 object-contain relative transition-all duration-300"
                    style={{
                      filter: 'drop-shadow(0 4px 12px rgba(0, 0, 0, 0.4))',
                    }}
                  />
                </div>
                <span className="mt-2 text-sm font-medium text-gray-300 group-hover:text-white transition-colors drop-shadow-md">
                  {mode.label}
                </span>
              </button>
            ))}
          </div>

          {/* --- ADD THIS TOGGLE SECTION --- */}
          <div className="mt-6 flex gap-2 bg-gray-800/80 p-1 rounded-xl border border-gray-600 backdrop-blur-sm">
            <button 
              onClick={() => setCombatMode('probabilistic')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${combatMode === 'probabilistic' ? 'bg-teal-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
            >
              🎲 Dice Rolls
            </button>
            <button 
              onClick={() => setCombatMode('deterministic')}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${combatMode === 'deterministic' ? 'bg-purple-600 text-white shadow-lg' : 'text-gray-400 hover:text-white'}`}
            >
              🧮 Deterministic
            </button>
          </div>
        </div>
        


        {/* Species Codex - Bottom Right */}
        <div className="absolute bottom-4 right-4 flex gap-2 z-20">
          {/* Stats Button */}
          <button
            onClick={() => setShowCodex('stats')}
            className="group relative transition-all duration-300 hover:scale-110 hover:-translate-y-1 w-12 h-12 bg-gray-800/80 rounded-full flex items-center justify-center border border-gray-700"
          >
            <span className="text-xl">📊</span>
          </button>
          {Object.values(SPECIES).map(species => (
            <button
              key={species.id}
              onClick={() => {
                setSelectedCodexSpecies(species.id);
                setShowCodex(true);
              }}
              className="group relative transition-all duration-300 hover:scale-110 hover:-translate-y-1"
            >
              <div 
                className="absolute inset-0 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100"
                style={{
                  background: `radial-gradient(circle, ${species.color.replace('ALPHA', '0.5')} 0%, transparent 70%)`,
                  transform: 'scale(1.5)',
                  filter: 'blur(8px)',
                }}
              />
              <img 
                src={getSpeciesImage(species.id, 10)}
                alt={species.name}
                className="w-12 h-12 object-contain relative drop-shadow-lg"
              />
            </button>
          ))}
        </div>
        
        {/* Career Stats Modal */}
        {showCodex === 'stats' && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCodex(false)}
          >
            <div 
              className="border border-gray-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
              style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)' }}
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-2xl font-black text-white mb-4 text-center">📊 Career Statistics</h2>
              
              {/* Overall Stats */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-teal-400">{careerStats.gamesPlayed}</div>
                  <div className="text-xs text-gray-500">Games</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">{careerStats.wins}</div>
                  <div className="text-xs text-gray-500">Wins</div>
                </div>
                <div className="bg-gray-800 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">{careerStats.losses}</div>
                  <div className="text-xs text-gray-500">Losses</div>
                </div>
              </div>
              
              {/* Win Rate */}
              {careerStats.gamesPlayed > 0 && (
                <div className="bg-gray-800 rounded-lg p-3 mb-4 text-center">
                  <div className="text-3xl font-bold text-yellow-400">
                    {Math.round((careerStats.wins / careerStats.gamesPlayed) * 100)}%
                  </div>
                  <div className="text-xs text-gray-500">Win Rate</div>
                </div>
              )}
              
              {/* By Difficulty */}
              <div className="mb-4">
                <div className="text-xs text-gray-400 mb-2 font-bold">BY DIFFICULTY</div>
                <div className="grid grid-cols-4 gap-2">
                  {['easy', 'normal', 'hard', 'insane'].map(d => (
                    <div key={d} className="bg-gray-800/50 rounded p-2 text-center">
                      <div className="text-xs text-gray-500 capitalize">{d}</div>
                      <div className="text-sm font-bold text-white">
                        {careerStats.byDifficulty[d]?.wins || 0}W
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Lifetime Stats */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-gray-800/50 rounded p-2 text-center">
                  <div className="text-lg font-bold text-purple-400">{careerStats.totalTerritoriesCaptured}</div>
                  <div className="text-xs text-gray-500">Total Captures</div>
                </div>
                <div className="bg-gray-800/50 rounded p-2 text-center">
                  <div className="text-lg font-bold text-orange-400">{careerStats.totalEnemiesEliminated}</div>
                  <div className="text-xs text-gray-500">Rivals Defeated</div>
                </div>
              </div>
              
              {/* Reset Button */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowCodex(false)}
                  className="flex-1 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    if (confirm('Reset all career statistics? This cannot be undone.')) {
                      const fresh = { gamesPlayed: 0, wins: 0, losses: 0, byDifficulty: { easy: { wins: 0, losses: 0 }, normal: { wins: 0, losses: 0 }, hard: { wins: 0, losses: 0 }, insane: { wins: 0, losses: 0 } }, bySpecies: {}, totalTerritoriesCaptured: 0, totalEnemiesEliminated: 0 };
                      setCareerStats(fresh);
                      localStorage.setItem('fungalConquest_careerStats', JSON.stringify(fresh));
                    }
                  }}
                  className="px-4 py-2 bg-red-900/50 hover:bg-red-800 border border-red-700 rounded-lg text-red-400 hover:text-red-300 text-sm transition-colors"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Species Codex Modal */}
        {showCodex === true && selectedCodexSpecies && (
          <div 
            className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowCodex(false)}
          >
            <div 
             // CHANGED: Removed "bg-gray-900/95" and added inline style for background
      className="border border-gray-700 rounded-2xl max-w-lg w-full p-6 shadow-2xl"
      style={{ backgroundColor: 'rgba(17, 24, 39, 0.95)' }} // Hard-coded dark gray
      onClick={e => e.stopPropagation()}
            >
              {(() => {
                const species = SPECIES[selectedCodexSpecies];
                return (
                  <>
                    {/* Header */}
                    <div className="flex items-center gap-4 mb-4">
                      <div className="relative">
                        <div 
                          className="absolute inset-0 rounded-full"
                          style={{
                            background: `radial-gradient(circle, ${species.color.replace('ALPHA', '0.4')} 0%, transparent 70%)`,
                            transform: 'scale(1.3)',
                            filter: 'blur(12px)',
                          }}
                        />
                        <img 
                          src={getSpeciesImage(selectedCodexSpecies, 10)}
                          alt={species.name}
                          className="w-24 h-24 object-contain relative"
                        />
                      </div>
                      <div>
                        <h2 className="text-2xl font-black text-white">{species.name}</h2>
                        <p className="text-sm text-gray-400">{species.passive}</p>
                      </div>
                    </div>
                    
                    {/* Combat Info */}
                    <div className="flex gap-4 mb-4">
                      {species.strongVs.length > 0 && (
                        <div className="flex-1 bg-green-900/30 border border-green-700/50 rounded-lg p-3">
                          <div className="text-xs text-green-400 font-bold mb-1">STRONG VS</div>
                          <div className="text-sm text-green-300">
                            {species.strongVs.map(s => SPECIES[s]?.name).join(', ')}
                          </div>
                        </div>
                      )}
                      {species.weakVs.length > 0 && (
                        <div className="flex-1 bg-red-900/30 border border-red-700/50 rounded-lg p-3">
                          <div className="text-xs text-red-400 font-bold mb-1">WEAK VS</div>
                          <div className="text-sm text-red-300">
                            {species.weakVs.map(s => SPECIES[s]?.name).join(', ')}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Lore */}
                    <div className="mb-4">
                      <h3 className="text-xs text-teal-400 font-bold mb-2 tracking-wider">LABORATORY NOTES</h3>
                      <p className="text-sm text-gray-300 leading-relaxed">{species.lore}</p>
                    </div>
                    
                    {/* Fun Fact */}
                    <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-3 mb-4">
                      <div className="text-xs text-yellow-400 font-bold mb-1">🔬 FUN FACT</div>
                      <p className="text-sm text-gray-300">{species.funFact}</p>
                    </div>
                    
                    {/* Close Button */}
                    <button
                      onClick={() => setShowCodex(false)}
                      className="w-full py-2 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg text-gray-300 hover:text-white transition-colors"
                    >
                      Close
                    </button>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        
        <div className="absolute bottom-6 text-xs text-gray-400 font-mono drop-shadow-md">
          v0.3.0 • MOBILE + SCORING UPDATE
        </div>
      </div>
    );
  }
  
  // ============ DIFFICULTY SELECT ============
  
  if (gamePhase === 'difficulty') {
    return (
      <div 
        className="flex flex-col items-center justify-center min-h-screen text-gray-100 p-4 relative overflow-hidden"
        style={{
          backgroundImage: `url(${UI_IMAGES.diffBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#1a1a2e',
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />

        <div className="relative z-10 flex flex-col items-center">
          {/* Difficulty Box Frame with buttons inside */}
          <div 
            className="relative flex flex-col items-center justify-center"
            style={{
              backgroundImage: `url(${UI_IMAGES.difficultyBox})`,
              backgroundSize: 'contain',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              width: '450px',
              height: '520px',
              paddingTop: '80px',
            }}
          >
            {/* Difficulty Buttons */}
            <div className="flex flex-col gap-2 w-full px-12">
              {[
                { id: 'easy', img: UI_IMAGES.buttons.easy },
                { id: 'normal', img: UI_IMAGES.buttons.normal },
                { id: 'hard', img: UI_IMAGES.buttons.hard },
                { id: 'insane', img: UI_IMAGES.buttons.insane },
              ].map(diff => (
                <button 
                  key={diff.id}
                  onClick={() => selectDifficulty(diff.id)} 
                  className="group relative transition-all duration-200 hover:scale-105 hover:brightness-110"
                >
                  <div 
                    className="absolute inset-0 transition-all duration-300 opacity-0 group-hover:opacity-100"
                    style={{
                      background: diff.id === 'insane' 
                        ? 'radial-gradient(circle, rgba(239, 68, 68, 0.4) 0%, transparent 70%)'
                        : diff.id === 'hard'
                        ? 'radial-gradient(circle, rgba(251, 191, 36, 0.4) 0%, transparent 70%)'
                        : diff.id === 'normal'
                        ? 'radial-gradient(circle, rgba(34, 197, 94, 0.4) 0%, transparent 70%)'
                        : 'radial-gradient(circle, rgba(56, 189, 248, 0.4) 0%, transparent 70%)',
                      filter: 'blur(12px)',
                    }}
                  />
                  <img 
                    src={diff.img} 
                    alt={diff.id}
                    className="w-full h-auto relative"
                  />
                </button>
              ))}
            </div>
          </div>
          
          {/* Back Button */}
          <button 
            onClick={() => setGamePhase('menu')}
            className="mt-4 group transition-all duration-200 hover:scale-105 hover:brightness-110"
          >
            <img 
              src={UI_IMAGES.buttons.back} 
              alt="Back to map select"
              className="h-10 w-auto"
            />
          </button>
        </div>
      </div>
    );
  }
  
  // ============ SPECIES SELECT ============
  
  if (gamePhase === 'species') {
     return (
    <div 
      className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-gray-100 p-4 font-sans"
      style={{
        backgroundImage: `url(${UI_IMAGES.speciesBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/30 pointer-events-none" />
        
        <div className="relative z-10 flex flex-col items-center">
          <p className="text-gray-300 mb-1 text-sm tracking-wide">Choose your strain for the experiment</p>
          <p className="text-xs text-gray-500 mb-6">
            Difficulty: <span className={`font-bold ${difficulty === 'insane' ? 'text-red-400' : difficulty === 'hard' ? 'text-orange-400' : 'text-teal-400'}`}>
              {DIFFICULTY[difficulty]?.icon} {DIFFICULTY[difficulty]?.name}
            </span>
          </p>
          
          <div className="flex flex-col gap-3 w-full max-w-lg">
            {Object.values(SPECIES).map(species => (
              <button 
                key={species.id}
                onClick={() => startGame(species.id)}
                className="group relative transition-all duration-200 hover:scale-[1.02] hover:brightness-110"
                style={{
                  backgroundImage: `url(${UI_IMAGES.buttons.species})`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  height: '110px', /* Adjust based on your button aspect ratio */
                }}
              >
                {/* Hover glow */}
                <div 
                  className="absolute inset-0 transition-all duration-300 opacity-0 group-hover:opacity-100 rounded-xl"
                  style={{
                    background: `radial-gradient(circle, ${species.color.replace('ALPHA', '0.3')} 0%, transparent 70%)`,
                    filter: 'blur(8px)',
                  }}
                />
                
                <div className="flex items-center gap-4 px-4 h-full relative z-10">
                  {/* Species Image */}
                  <img 
                    src={getSpeciesImage(species.id, 8)}
                    alt={species.name}
                    className="w-12 h-12 object-contain drop-shadow-md"
                  />
                  
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-base">{species.name}</h3>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: getSpeciesColor(species.id, 1) }} />
                    </div>
                    <div className="text-xs text-gray-400">{species.passive}</div>
                    <div className="text-[10px] font-bold text-green-400 uppercase tracking-wider mt-1">
                      {species.strongVs.length > 0 && `Strong vs ${species.strongVs.map(s => SPECIES[s]?.name).join(', ')}`}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
          
          {/* Back Button */}
          <button 
            onClick={() => setGamePhase('difficulty')}
            className="mt-6 group transition-all duration-200 hover:scale-105 hover:brightness-110"
          >
            <img 
              src={UI_IMAGES.buttons.back} 
              alt="Back to Difficulty"
              className="h-10 w-auto"
            />
          </button>
        </div>
      </div>
    );
  }

// ============ NEW FULL-SCREEN GAME LAYOUT (OPTIMIZED) ============
  
  return (
    <div className="game-window">
      
      {/* LAYER 1: THE GAME WORLD */}
      <div className="game-viewport">
        <div 
          className="relative"
          style={ (mapType === 'outbreak' || mapType === 'open') ? {
  // Use a variable size: Outbreak is 1600, Open is 800
  width: (mapType === 'outbreak' ? 1600 : 800) * zoomLevel,
  height: (mapType === 'outbreak' ? 1600 : 800) * zoomLevel,
  transform: `translate(${viewportOffset.x}px, ${viewportOffset.y}px)`,
  transition: isDragging ? 'none' : 'transform 0.1s ease-out',
  cursor: isDragging ? 'grabbing' : 'grab',
  position: 'absolute',
  left: '50%',
  top: '50%',
  // Center it based on its own size
  marginLeft: (mapType === 'outbreak' ? -800 : -400) * zoomLevel,
  marginTop: (mapType === 'outbreak' ? -800 : -400) * zoomLevel,
} : {
  // Keeping 'ring' or 'chokepoint' as fixed center for now
  width: 800, 
  height: 800,
  transform: `scale(${Math.min(window.innerWidth / 850, window.innerHeight / 850, 1)})` 
}}
          
          // --- 1. FIXED SCROLL ZOOM ---
          onWheel={(e) => {
            if (mapType === 'outbreak' || mapType === 'open'){
              e.preventDefault(); // Stop web page from scrolling
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              const minZoom = isMobile ? 0.2 : 0.4; // Allow zooming out further on mobile
              setZoomLevel(z => Math.max(minZoom, Math.min(2, z + delta)));
            }
          }}
          
          // Mouse/Touch Handlers
          onMouseDown={(e) => { 
            if (mapType === 'outbreak' || mapType === 'open'){
              setIsDragging(true); 
              setDragStart({ x: e.clientX - viewportOffset.x, y: e.clientY - viewportOffset.y }); 
            }
          }}
          onMouseMove={(e) => { 
            if (isDragging && mapType === 'outbreak') { 
              const newX = e.clientX - dragStart.x; 
              const newY = e.clientY - dragStart.y;
              setViewportOffset({ x: newX, y: newY });
            }
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
          onTouchStart={(e) => {
            if (mapType !== 'outbreak') return;
            if (e.touches.length === 2) {
               e.preventDefault(); // Stop browser zoom
               const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
               setIsPinching(true);
               setLastPinchDistance(dist);
               setIsDragging(false);
            } else if (e.touches.length === 1) {
               setIsDragging(true);
               setDragStart({ x: e.touches[0].clientX - viewportOffset.x, y: e.touches[0].clientY - viewportOffset.y });
            }
          }}
          onTouchMove={(e) => {
             if (mapType !== 'outbreak') return;
             if (isPinching && e.touches.length === 2) {
                 e.preventDefault();
                 const dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
                 if (lastPinchDistance > 0) {
                     const delta = (dist - lastPinchDistance) * 0.005; 
                     setZoomLevel(z => Math.max(0.2, Math.min(2, z + delta)));
                 }
                 setLastPinchDistance(dist);
             } else if (isDragging && e.touches.length === 1) {
                 const newX = e.touches[0].clientX - dragStart.x; 
                 const newY = e.touches[0].clientY - dragStart.y;
                 setViewportOffset({ x: newX, y: newY });
             }
          }}
          onTouchEnd={() => { setIsDragging(false); setIsPinching(false); }}
        >
            <svg 
                width="100%" 
                height="100%" 
                viewBox={mapType === 'outbreak' ? "0 0 1600 1600" : "0 0 800 800"}
                style={{
                    background: BACKGROUND_IMAGE 
                    ? `url(${BACKGROUND_IMAGE}) center/cover no-repeat`
                    : 'radial-gradient(ellipse at center, #f5f0e1 0%, #e8e0c8 70%, #d4c9a8 100%)',
                    borderRadius: '50%',
                    // Removed the heavy box-shadow from the container too
                    boxShadow: '0 0 20px rgba(0,0,0,0.5)' 
                }}
            >
                {/* Barriers */}
                {barriers.map((bar, i) => (
                    <line key={`barrier-${i}`} x1={bar.start.x} y1={bar.start.y} x2={bar.end.x} y2={bar.end.y} stroke="rgba(255, 255, 255, 0.5)" strokeWidth="12" strokeLinecap="round" />
                ))}
                
                {/* Hyphae */}
                {territories.map(t => t.neighbors.map(n => {
                    const neighbor = territories.find(x => x.id === n);
                    if (!neighbor || n < t.id || !t.owner || t.owner !== neighbor.owner) return null;
                    return <line key={`${t.id}-${n}`} x1={t.x} y1={t.y} x2={neighbor.x} y2={neighbor.y} stroke={getSpeciesColor(t.owner, 0.3)} strokeWidth="3" strokeDasharray="5,5" />;
                }))}

                 {/* Supply Routes (Optimization: Reduced shadow intensity) */}
                 {Object.entries(supplyRoutes).map(([sourceId, destId]) => {
                    const source = territories.find(t => t.id === parseInt(sourceId));
                    const dest = territories.find(t => t.id === destId);
                    if (!source || !dest) return null;
                    const path = findPathBetween(parseInt(sourceId), destId, playerSpecies, territories);
                    if (!path || path.length < 2) return null;
                    return path.slice(0, -1).map((nodeId, i) => {
                      const from = territories.find(t => t.id === nodeId);
                      const to = territories.find(t => t.id === path[i + 1]);
                      if (!from || !to) return null;
                      return (
                        <line key={`supply-${sourceId}-${i}`} x1={from.x} y1={from.y} x2={to.x} y2={to.y}
                          stroke="rgba(20, 184, 166, 0.7)" strokeWidth="4" strokeLinecap="round"
                        >
                            <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
                        </line>
                      );
                    });
                  })}

                {/* Valid Target Markers */}
                {selected !== null && !isSettingSupplyRoute && validTargets.map(id => {
                    const t = territories.find(x => x.id === id);
                    if (!t) return null;
                    const isFriendly = t.owner === playerSpecies;
                    const isAdjacent = selectedTerritory?.neighbors.includes(id);
                    return <circle key={`target-${id}`} cx={t.x} cy={t.y} r={t.radius + 18} fill="none" 
                        stroke={isFriendly && !isAdjacent ? "rgba(100,200,255,0.6)" : "rgba(255,200,0,0.5)"} 
                        strokeWidth="2" strokeDasharray="4,4" />;
                })}

                {/* AI Arrow */}
                {aiAction && (() => {
                    const from = territories.find(t => t.id === aiAction.from);
                    const to = territories.find(t => t.id === aiAction.to);
                    if (!from || !to) return null;
                    return (
                        <g>
                           <defs><marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill="#ff6b6b" /></marker></defs>
                           <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#ff6b6b" strokeWidth="4" markerEnd="url(#arrowhead)" />
                        </g>
                    );
                })()}

                {/* Territory Nodes */}
                {territories.map(t => {
                    const growthBonus = Math.min(t.biomass * 1.5, 25);
                    const currentRadius = t.radius + growthBonus;
                    const randomRotation = (t.id * 57) % 360;
                    
                    const isSelected = selected === t.id;
                    const isTarget = target === t.id;
                    const isAiFrom = aiAction?.from === t.id;
                    const isAiTo = aiAction?.to === t.id;

                    // --- 2. PERFORMANCE FIX: Only apply filters when active ---
                    let activeFilter = 'none';
                    if (isSelected) activeFilter = 'drop-shadow(0 0 8px #00ffff)';
                    else if (isTarget) activeFilter = 'drop-shadow(0 0 8px #ff0000)';
                    else if (isAiFrom) activeFilter = 'drop-shadow(0 0 6px #ff6b6b)';
                    else if (isAiTo) activeFilter = 'drop-shadow(0 0 6px #ffd93d)';
                    
                    // Note: We removed the 'drop-shadow' for normal idle nodes. 
                    // This stops the fan from spinning!

                    return (
                        <g key={t.id} onClick={() => handleTerritoryClick(t)} style={{ cursor: 'pointer' }}>
                            {/* Terrain Glow */}
                            {getTerrainGlow(t.terrain) && <circle cx={t.x} cy={t.y} r={t.radius + 10} fill={getTerrainGlow(t.terrain)} />}
                            
                            {/* The Colony Image */}
                            {t.owner ? (
                                <image 
                                    href={getSpeciesImage(t.owner, t.biomass)} 
                                    x={t.x - currentRadius} y={t.y - currentRadius} 
                                    height={currentRadius * 2} width={currentRadius * 2} 
                                    style={{ 
                                        transform: `rotate(${randomRotation}deg)`, 
                                        transformOrigin: 'center', 
                                        transformBox: 'fill-box',
                                        filter: activeFilter, // USING THE OPTIMIZED FILTER
                                        transition: 'all 0.2s'
                                    }} 
                                />
                            ) : (
                                <path d={generateBlobPath(t.x, t.y, currentRadius, t.id)} fill="rgba(180, 180, 170, 0.2)" stroke="rgba(0,0,0,0.1)" strokeWidth="1" />
                            )}
                            
                            {/* Text Labels */}
                            {t.owner && <text x={t.x} y={t.y + 4} textAnchor="middle" fill="#fff" fontSize="12" fontWeight="bold" style={{ textShadow: '1px 1px 2px black' }}>{t.biomass}</text>}
                            {getTerrainIcon(t.terrain) && <text x={t.x} y={t.y - t.radius - 3} textAnchor="middle" fontSize="9">{getTerrainIcon(t.terrain)}</text>}
                        </g>
                    );
                })}
            </svg>
        </div>
      </div>

      {/* LAYER 2: THE HUD (UI OVERLAY) */}
      <div className="ui-overlay">
        
        {/* TOP HUD */}
        <div className="ui-top">
            <div className="player-card">
                 <div className="relative">
                    <img src={getSpeciesImage(playerSpecies, 10)} alt="You" className="w-10 h-10 object-contain" />
                    <div className="absolute -bottom-1 -right-1 bg-teal-500 text-[10px] text-white px-1 rounded-full border border-gray-900">YOU</div>
                 </div>
                 <div>
                    <div className="font-bold text-teal-400 text-sm">{SPECIES[playerSpecies]?.name}</div>
                    <div className="text-xs text-gray-300">
                        {stats.find(s => s.species === playerSpecies)?.territories} tiles • 
                        <span className="text-green-400"> +{calculateGrowth(playerSpecies)}/turn</span>
                    </div>
                    {(mutations[playerSpecies]?.length > 0) && (
                        <div className="text-[10px] text-gray-500 flex gap-1 mt-1">
                            {mutations[playerSpecies].map(m => <span key={m}>{MUTATIONS.find(x => x.id === m)?.icon}</span>)}
                        </div>
                    )}
                 </div>
            </div>

            <div className="enemy-list">
                {stats.filter(s => s.species !== playerSpecies).map(s => (
                    <div key={s.species} className="enemy-pill">
                        <img src={getSpeciesImage(s.species, 5)} alt={s.species} className="w-6 h-6 mb-1 opacity-80" />
                        <span className="text-[10px] text-gray-400 font-mono">{s.biomass}</span>
                    </div>
                ))}
            </div>
        </div>

        {/* ALERTS */}
        {message && (
             <div className="pointer-events-none absolute top-20 left-0 w-full flex justify-center z-10">
                 <div className="bg-black/60 text-white px-4 py-1 rounded-full backdrop-blur-sm text-sm border border-white/10 shadow-lg">
                    {message}
                 </div>
             </div>
        )}
        
        {/* ZOOM CONTROLS (Manual buttons for mobile) */}
        {(mapType === 'outbreak' || mapType === 'open') && (
             <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex flex-col gap-2 bg-gray-900/80 p-2 rounded-lg border border-gray-700 pointer-events-auto">
                 <button onClick={() => setZoomLevel(z => Math.min(2, z + 0.25))} className="w-8 h-8 flex items-center justify-center text-white bg-gray-700 rounded hover:bg-gray-600">+</button>
                 <div className="text-xs text-center text-gray-400">{Math.round(zoomLevel * 100)}%</div>
                 <button onClick={() => setZoomLevel(z => Math.max(0.2, z - 0.25))} className="w-8 h-8 flex items-center justify-center text-white bg-gray-700 rounded hover:bg-gray-600">-</button>
             </div>
        )}

        {/* BOTTOM HUD */}
        <div className="ui-bottom">
            <div className="action-bar">
                 <button 
                    onClick={() => setGameOver({ winner: 'AI' })} 
                    className="p-3 text-gray-400 hover:text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                    title="Forfeit"
                 >
                    🏳️
                 </button>
                 
                 <div className="w-px bg-gray-700 mx-1"></div>

                 <button 
                    onClick={endTurn} 
                    disabled={currentPlayer !== playerSpecies || isInCombat}
                    className="bg-gradient-to-r from-teal-600 to-teal-500 hover:from-teal-500 hover:to-teal-400 text-white px-8 py-3 rounded-lg font-bold shadow-lg disabled:opacity-50 disabled:grayscale transition-all active:scale-95 flex items-center gap-2"
                 >
                    <span>End Turn</span>
                    <span className="text-lg">➜</span>
                 </button>
            </div>
        </div>
        
        {/* MODALS */}
        
        {showMutationPicker && (
            <div className="absolute inset-0 bg-black/80 flex items-center justify-center pointer-events-auto z-50">
                <div className="bg-gray-800 text-white rounded-xl p-6 max-w-sm w-full border border-gray-700 shadow-2xl">
                    <h3 className="text-xl font-bold mb-4 text-center">🧬 Evolve Strain</h3>
                    <div className="space-y-2">
                        {mutationChoices.map(m => (
                            <button key={m.id} onClick={() => selectMutation(m.id)} className="w-full p-3 bg-gray-700 hover:bg-teal-900/50 hover:border-teal-500 border border-transparent rounded-lg text-left transition-all group">
                                <div className="font-bold text-teal-300 group-hover:text-teal-200">{m.icon} {m.name}</div>
                                <div className="text-xs text-gray-400 group-hover:text-gray-300">{m.desc}</div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        )}

        {isInCombat && combatLog.length > 0 && (
             <div className="absolute top-24 left-1/2 transform -translate-x-1/2 bg-gray-900/90 text-white p-4 rounded-xl border border-gray-700 shadow-2xl pointer-events-auto min-w-[240px] z-50">
             <div className="text-center font-bold text-teal-400 mb-2">⚔️ Combat Active</div>
              <div className="max-h-32 overflow-y-auto space-y-1 mb-2 pr-1">
                {combatLog.map((round, i) => (
                  <div key={i} className="text-xs flex justify-between border-b border-gray-800 pb-1">
                    <span className="text-teal-400">Atk [{round.attackDice.join(',')}]</span>
                    <span className="text-orange-400">Def [{round.defenseDice.join(',')}]</span>
                  </div>
                ))}
              </div>
              <div className="text-center font-bold text-sm">
                {combatLog[combatLog.length - 1]?.defenderRemaining <= 0 ? '🎉 Victory!' : '💀 Repelled'}
              </div>
            </div>
        )}
        
        {showMoveSelector && selectedTerritory && (
            <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-auto z-40">
                <div className="bg-gray-800 border border-gray-600 text-white rounded-xl p-6 shadow-2xl w-80 max-h-[90vh] overflow-y-auto">
                   
                   <div className="flex justify-between items-center mb-4">
                       <h3 className="font-bold">
                           {target && territories.find(t=>t.id===target)?.owner && territories.find(t=>t.id===target)?.owner !== playerSpecies ? '⚔️ Attack' : 'Move Biomass'}
                       </h3>
                       <button onClick={() => setShowMoveSelector(false)} className="text-gray-400 hover:text-white">✕</button>
                   </div>

                   {(() => {
                        const targetTerr = territories.find(t => t.id === target);
                        const isAttack = targetTerr?.owner && targetTerr?.owner !== playerSpecies;
                        if (!isAttack) return null;
                        
                        const pincerCount = targetTerr.neighbors.filter(n => {
                          const neighbor = territories.find(t => t.id === n);
                          return neighbor && neighbor.owner === playerSpecies && neighbor.id !== selected;
                        }).length;

                        const battleProb = calculateDetailedWinProbability(
                          moveAmount, targetTerr.biomass, playerSpecies, targetTerr.owner,
                          mutations[playerSpecies] || [], mutations[targetTerr.owner] || [], targetTerr.terrain, pincerCount, combatMode === 'deterministic'
                        );

                        return (
                            <div className="bg-gray-900/50 rounded-lg p-2 mb-4 text-center">
                                <div className="text-2xl font-bold mb-1" style={{color: battleProb.probability > 60 ? '#4ade80' : battleProb.probability > 40 ? '#facc15' : '#f87171'}}>
                                    {battleProb.probability}% <span className="text-sm text-gray-400">Win Rate</span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    {battleProb.attackerDice} dice vs {battleProb.defenderDice} dice
                                </div>
                            </div>
                        );
                   })()}

                   <div className="flex items-center justify-center gap-4 mb-6">
                      <button onClick={() => setMoveAmount(Math.max(1, moveAmount - 1))} className="w-10 h-10 bg-gray-700 rounded-full text-xl hover:bg-gray-600">-</button>
                      <div className="text-center">
                          <span className="text-3xl font-mono font-bold block">{moveAmount}</span>
                          <span className="text-[10px] text-gray-500">Sending</span>
                      </div>
                      <button onClick={() => setMoveAmount(Math.min(maxMove, moveAmount + 1))} className="w-10 h-10 bg-gray-700 rounded-full text-xl hover:bg-gray-600">+</button>
                   </div>
                   
                   <button onClick={executeMove} className="w-full bg-teal-600 hover:bg-teal-500 py-3 rounded-lg font-bold shadow-lg mb-3">
                       CONFIRM
                   </button>
                   
                   {target && territories.find(t=>t.id===target)?.owner === playerSpecies && (
                       <div className="border-t border-gray-700 pt-3 mt-2">
                           <div className="text-xs text-gray-500 text-center mb-2">Advanced Logistics</div>
                           <div className="flex gap-2">
                               <button onClick={() => { setSupplyRoutes(prev => ({ ...prev, [selected]: target })); setShowMoveSelector(false); setTarget(null); setSelected(null); }} 
                                   className="flex-1 py-2 bg-blue-900/50 hover:bg-blue-800 text-blue-200 text-xs rounded border border-blue-800">
                                   📦 Route Only
                               </button>
                               <button onClick={() => { executeMove(); setSupplyRoutes(prev => ({ ...prev, [selected]: target })); }} 
                                   className="flex-1 py-2 bg-blue-900/50 hover:bg-blue-800 text-blue-200 text-xs rounded border border-blue-800">
                                   📦 Move & Route
                               </button>
                           </div>
                       </div>
                   )}
                </div>
            </div>
        )}

        {showEvent && currentEvent && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center pointer-events-auto z-50">
            <div className="bg-purple-900/90 text-white rounded-lg p-6 shadow-xl text-center border border-purple-500">
              <div className="text-4xl mb-2">🌍</div>
              <div className="font-bold text-xl mb-1">{currentEvent.name}</div>
              <div className="text-sm text-purple-200">{currentEvent.desc}</div>
            </div>
          </div>
        )}
        
        {gameOver && (
          <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-4 pointer-events-auto z-50">
            <div className="bg-gray-800 border-2 border-gray-600 rounded-xl p-6 max-w-sm w-full text-center shadow-2xl">
              <div className="text-3xl font-bold mb-2" style={{ color: getSpeciesColor(gameOver.winner, 1) }}>
                  {gameOver.winner === playerSpecies ? '🏆 VICTORY!' : '💀 DEFEATED'}
              </div>
              <div className="text-gray-400 mb-6">
                  {SPECIES[gameOver.winner]?.name} dominates the dish!
              </div>
              {!isSpectatorMode && (
                  <div className="text-5xl font-black text-teal-400 mb-2">
                      {calculateGameScore(gameStats, gameOver.winner === playerSpecies, turn, difficulty).toLocaleString()}
                  </div>
              )}
              <div className="text-xs text-gray-500 mb-6">FINAL SCORE</div>
              
              <button 
                onClick={() => { setGamePhase('menu'); setIsSpectatorMode(false); setPreviousAlive([]); }} 
                className="w-full py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-bold"
              >
                Back to Menu
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default FungalConquest;